import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Copy, Search } from "lucide-react";

/* ================== HELPERS DATA ================== */
const DEVELOPER_ROLES = [
  "Frontend Developer",
  "Backend Developer",
  "Full Stack Developer",
  "Mobile App Developer",
  "Web Developer",
  "UI Engineer",
  "React Developer",
  "React Native Developer",
  "Node.js Developer",
  "Product Engineer",
  "Freelancer",
  "Startup Engineer",
  "Junior Developer",
  "Senior Developer",
  "Tech Lead",
];
const TECHNOLOGIES = [
  "JavaScript",
  "TypeScript",
  "React",
  "Next.js",
  "React Native",
  "Expo",
  "Node.js",
  "Express",
  "HTML",
  "CSS",
  "Tailwind CSS",
  "Browser API",
  "Web API",
  "Firebase",
  "REST API",
];
const PLATFORMS = ["Web", "Mobile", "Backend", "Cross-Platform"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];

const HELPERS = [
  /* ================== TIME & DATE ================== */

  /* ================================
   📝 STRING & TEXT HELPERS
================================ */

  {
    category: "📝 String & Text",
    name: "capitalize",
    desc: "Capitalizes first letter of string",

    // ✅ JavaScript (Universal)
    javascript: `
const capitalize = (str) => {
  if (typeof str !== "string" || !str.length) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const capitalize = (str: string): string => {
  if (!str.length) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
`,

    example: `capitalize("hello") → "Hello"`,

    // 👨‍💻 Who should use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "UI Engineer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies where this is commonly used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "React Native",
      "Node.js",
      "Vue",
      "Angular",
    ],

    // 📍 Typical use cases
    useCases: [
      "Formatting user names",
      "UI labels & headings",
      "Form data normalization",
      "Content presentation",
    ],
  },
  {
    category: "📝 String & Text",
    name: "toTitleCase",
    desc: "Converts a string into Title Case (each word capitalized)",

    // ✅ JavaScript (Universal)
    javascript: `
const toTitleCase = (str) => {
  if (typeof str !== "string") return "";
  return str.replace(/\\w\\S*/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const toTitleCase = (str: string): string => {
  return str.replace(/\\w\\S*/g, (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
};
`,

    example: `toTitleCase("hello world") → "Hello World"`,

    // 👨‍💻 Who should use this
    roles: [
      "Frontend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "UI/UX Engineer",
      "Content Platform Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "React Native",
      "Vue",
      "Angular",
      "Node.js",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Page titles & headings",
      "Blog titles",
      "User-entered text formatting",
      "Product names",
      "SEO-friendly UI labels",
    ],
  },
  {
    category: "📝 String & Text",
    name: "truncate",
    desc: "Truncates a long string and appends ellipsis (…) when it exceeds a given length",

    // ✅ JavaScript (Universal)
    javascript: `
const truncate = (str, len = 50) => {
  if (typeof str !== "string") return "";
  return str.length > len ? str.slice(0, len) + "…" : str;
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const truncate = (str: string, len: number = 50): string => {
  return str.length > len ? str.slice(0, len) + "…" : str;
};
`,

    example: `truncate("This is long text", 10) → "This is lo…"`,

    // 👨‍💻 Who should use this
    roles: [
      "Frontend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "UI Engineer",
      "Dashboard Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "React Native",
      "Vue",
      "Angular",
      "Node.js",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Card descriptions",
      "Blog previews",
      "Table cell content",
      "Notifications & alerts",
      "Product descriptions",
      "Mobile UI text overflow handling",
    ],
  },
  {
    category: "📝 String & Text",
    name: "reverseString",
    desc: "Reverses the characters of a string",

    // ✅ JavaScript (Universal)
    javascript: `
const reverseString = (str) => {
  if (typeof str !== "string") return "";
  return str.split("").reverse().join("");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const reverseString = (str: string): string => {
  return str.split("").reverse().join("");
};
`,

    example: `reverseString("abc") → "cba"`,

    // 👨‍💻 Who can use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "JavaScript Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "React Native",
      "Vue",
      "Angular",
      "Node.js",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Interview coding questions",
      "String manipulation utilities",
      "Palindrome checks",
      "Text processing tools",
      "Learning core JavaScript concepts",
      "Data transformation logic",
    ],
  },
  {
    category: "📝 String & Text",
    name: "wordCount",
    desc: "Counts the number of words in a string",

    // ✅ JavaScript (Universal)
    javascript: `
const wordCount = (str) => {
  if (typeof str !== "string") return 0;
  return str.trim().split(/\\s+/).length;
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const wordCount = (str: string): number => {
  return str.trim().split(/\\s+/).length;
};
`,

    example: `wordCount("hello world dev") → 3`,

    // 👨‍💻 Who can use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Content Platform Developer",
      "Mobile App Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Text editor word count",
      "Blog & CMS platforms",
      "Input validation (min/max words)",
      "SEO content analysis",
      "Learning string manipulation",
      "Interview coding problems",
    ],
  },
  {
    category: "📝 String & Text",
    name: "stripHtml",
    desc: "Removes HTML tags from a string and returns clean text",

    // ✅ JavaScript (Universal)
    javascript: `
const stripHtml = (html) => {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, "");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, "");
};
`,

    example: `stripHtml("<p>Hello <strong>World</strong></p>") → "Hello World"`,

    // 👨‍💻 Who can use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "CMS / Platform Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Sanitizing user input",
      "Displaying plain text previews",
      "Removing HTML from CMS content",
      "Search indexing & SEO cleanup",
      "Preventing layout breaks",
      "Interview string manipulation questions",
    ],
  },
  {
    category: "📝 String & Text",
    name: "slugify",
    desc: "Creates a clean, URL-friendly slug from any string",

    // ✅ JavaScript (Universal)
    javascript: `
const slugify = (str) => {
  if (typeof str !== "string") return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\\w\\s-]/g, "")
    .replace(/[\\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\\w\\s-]/g, "")
    .replace(/[\\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};
`,

    example: `slugify("Hello World 2025") → "hello-world-2025"`,

    // 👨‍💻 Who can use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "SEO Engineer",
      "CMS / Platform Developer",
      "Mobile App Developer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "SEO-friendly URLs",
      "Blog post slugs",
      "Dynamic routing (Next.js / Nuxt)",
      "Product & category URLs (e-commerce)",
      "File naming",
      "CMS content URLs",
      "Interview string manipulation problems",
    ],
  },
  {
    category: "📝 String & Text",
    name: "isPalindrome",
    desc: "Checks whether a string reads the same forwards and backwards (ignores case & non-alphanumerics)",

    // ✅ JavaScript (Universal)
    javascript: `
const isPalindrome = (str) => {
  if (typeof str !== "string") return false;
  const clean = str.toLowerCase().replace(/[^a-z0-9]/g, "");
  return clean === clean.split("").reverse().join("");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const isPalindrome = (str: string): boolean => {
  const clean = str.toLowerCase().replace(/[^a-z0-9]/g, "");
  return clean === clean.split("").reverse().join("");
};
`,

    example: `
isPalindrome("madam") → true
isPalindrome("A man, a plan, a canal: Panama") → true
isPalindrome("hello") → false
`,

    // 👨‍💻 Who can use this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Competitive Programmer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world & interview use cases
    useCases: [
      "Interview string problems",
      "Input validation & puzzles",
      "Learning regex & string normalization",
      "Competitive programming practice",
      "Utility libraries",
    ],
  },
  {
    category: "📝 String & Text",
    name: "maskEmail",
    desc: "Masks an email address for privacy by hiding middle characters",

    // ✅ JavaScript (Universal – Web, Backend, Mobile)
    javascript: `
const maskEmail = (email) => {
  if (typeof email !== "string") return "";
  return email.replace(/(.{2}).+(@.+)/, "$1***$2");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const maskEmail = (email: string): string => {
  return email.replace(/(.{2}).+(@.+)/, "$1***$2");
};
`,

    example: `
maskEmail("test@gmail.com") → "te***@gmail.com"
maskEmail("john.doe@company.com") → "jo***@company.com"
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Security Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "User profile privacy",
      "Account settings screens",
      "Admin dashboards",
      "Audit logs & reports",
      "Masking sensitive data in UI",
    ],
  },
  {
    category: "📝 String & Text",
    name: "maskPhone",
    desc: "Masks a phone number by hiding digits except the last ones for privacy",

    // ✅ JavaScript (Universal – Web, Backend, Mobile)
    javascript: `
const maskPhone = (phone) => {
  if (!phone) return "";
  return phone.toString().replace(/\\d(?=\\d{2})/g, "*");
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const maskPhone = (phone: string | number): string => {
  return phone.toString().replace(/\\d(?=\\d{2})/g, "*");
};
`,

    example: `
maskPhone("9876543210") → "********10"
maskPhone("123456789") → "*******89"
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Security Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "NestJS",
      "React Native",
      "Expo",
      "Flutter (logic equivalent)",
    ],

    // 📍 Real-world use cases
    useCases: [
      "User profile privacy",
      "OTP / login screens",
      "Order & delivery details",
      "Customer support dashboards",
      "Audit logs & admin panels",
    ],
  },
  /* ================================
   🧠 ARRAY HELPERS
================================ */

  {
    category: "🧠 Array Helpers",
    name: "unique",
    desc: "Removes duplicate values from an array",

    // ✅ JavaScript (Universal)
    javascript: `
const unique = (arr) => {
  return [...new Set(arr)];
};
`,

    // ✅ TypeScript (Type-safe & reusable)
    typescript: `
const unique = <T>(arr: T[]): T[] => {
  return Array.from(new Set(arr));
};
`,

    example: `
unique([1, 2, 2, 3]) → [1, 2, 3]
unique(["a", "b", "a"]) → ["a", "b"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner"],

    // 🛠️ Technologies where this is commonly used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "NestJS",
      "React Native",
      "Expo",
      "Backend APIs",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Removing duplicate API results",
      "Filtering dropdown options",
      "Tag & category systems",
      "Deduplicating IDs or values",
      "Analytics & reporting",
      "Client-side caching",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "chunk",
    desc: "Splits an array into smaller chunks of a given size",

    // ✅ JavaScript (Universal)
    javascript: `
const chunk = (arr, size) => {
  if (!Array.isArray(arr) || size <= 0) return [];
  return Array.from(
    { length: Math.ceil(arr.length / size) },
    (_, i) => arr.slice(i * size, i * size + size)
  );
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const chunk = <T>(arr: T[], size: number): T[][] => {
  if (!Array.isArray(arr) || size <= 0) return [];
  return Array.from(
    { length: Math.ceil(arr.length / size) },
    (_, i) => arr.slice(i * size, i * size + size)
  );
};
`,

    example: `
chunk([1, 2, 3, 4, 5], 2) → [[1, 2], [3, 4], [5]]
chunk(["a","b","c","d"], 3) → [["a","b","c"], ["d"]]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "NestJS",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Pagination & infinite scroll",
      "Grid layouts (rows & columns)",
      "Batch API requests",
      "Splitting large datasets",
      "Carousel & slider logic",
      "Processing data in chunks (performance)",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "flatten",
    desc: "Flattens a deeply nested array into a single-level array",

    // ✅ JavaScript (Universal)
    javascript: `
const flatten = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.flat(Infinity);
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const flatten = <T>(arr: any[]): T[] => {
  if (!Array.isArray(arr)) return [];
  return arr.flat(Infinity) as T[];
};
`,

    example: `
flatten([1, [2, [3, [4]]]]) → [1, 2, 3, 4]
flatten([[1,2], [3,4], [5]]) → [1,2,3,4,5]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "NestJS",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Normalizing API responses",
      "Flattening nested menu or category data",
      "Processing tree-like structures",
      "Analytics & reporting pipelines",
      "Search indexing",
      "UI lists rendered from nested data",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "shuffle",
    desc: "Randomly shuffles elements of an array using Fisher–Yates algorithm",

    // ✅ JavaScript (Universal – browser & Node.js)
    javascript: `
const shuffle = (arr) => {
  if (!Array.isArray(arr)) return [];
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const shuffle = <T>(arr: T[]): T[] => {
  if (!Array.isArray(arr)) return [];
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};
`,

    example: `
shuffle([1, 2, 3, 4]) → [3, 1, 4, 2] // random order
shuffle(["a", "b", "c"]) → ["c", "a", "b"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Game Developer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Quiz or MCQ randomization",
      "Shuffling playlists or feeds",
      "A/B testing variations",
      "Randomized recommendations",
      "Games & simulations",
      "Interview coding questions",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "randomElement",
    desc: "Returns a random element from an array safely",

    // ✅ JavaScript (Universal)
    javascript: `
const randomElement = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined;
  return arr[Math.floor(Math.random() * arr.length)];
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const randomElement = <T>(arr: T[]): T | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined;
  return arr[Math.floor(Math.random() * arr.length)];
};
`,

    example: `
randomElement(["a", "b", "c"]) → "b"
randomElement([10, 20, 30]) → 20
randomElement([]) → undefined
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Game Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Random quiz/question selection",
      "Feature flag experiments",
      "A/B testing variants",
      "Random banner or ad rotation",
      "Game mechanics (loot, cards)",
      "Interview coding tasks",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "compact",
    desc: "Removes all falsy values (false, 0, '', null, undefined, NaN) from an array",

    // ✅ JavaScript (Universal)
    javascript: `
const compact = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(Boolean);
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const compact = <T>(arr: Array<T | null | undefined | false | 0 | "">): T[] => {
  return arr.filter(Boolean) as T[];
};
`,

    example: `
compact([0, 1, false, 2, "", 3, null, undefined]) → [1, 2, 3]
compact(["a", "", "b"]) → ["a", "b"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "React Native",
      "Expo",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Cleaning API response arrays",
      "Removing empty form values",
      "Filtering optional UI items",
      "Pre-processing analytics data",
      "Preparing payload before API calls",
      "Interview array manipulation questions",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "intersection",
    desc: "Finds common elements between two arrays",

    // ✅ JavaScript (Universal)
    javascript: `
const intersection = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return [];
  return a.filter(item => b.includes(item));
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const intersection = <T>(a: T[], b: T[]): T[] => {
  return a.filter(item => b.includes(item));
};
`,

    example: `
intersection([1, 2, 3], [2, 3, 4]) → [2, 3]
intersection(["react", "js"], ["js", "ts"]) → ["js"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Data Engineer",
      "Mobile App Developer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Finding common permissions between roles",
      "Filtering shared tags or categories",
      "Matching user-selected filters",
      "Comparing allowed vs requested scopes",
      "Analytics data comparison",
      "Interview array logic questions",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "difference",
    desc: "Finds elements present in first array but not in second array",

    // ✅ JavaScript (Universal)
    javascript: `
const difference = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return [];
  return a.filter(item => !b.includes(item));
};
`,

    // ✅ TypeScript (Type-safe)
    typescript: `
const difference = <T>(a: T[], b: T[]): T[] => {
  return a.filter(item => !b.includes(item));
};
`,

    example: `
difference([1, 2, 3], [2, 3]) → [1]
difference(["admin", "user"], ["user"]) → ["admin"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Common technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Removing already selected items from list",
      "Permission & role difference handling",
      "Filtering excluded categories",
      "Syncing local vs server data",
      "Finding missing values",
      "Common interview array problems",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "pluck",
    desc: "Extracts values of a specific key from an array of objects",

    // ✅ JavaScript (Universal)
    javascript: `
const pluck = (arr, key) => {
  if (!Array.isArray(arr)) return [];
  return arr.map(item => item?.[key]);
};
`,

    // ✅ TypeScript (Type-safe & generic)
    typescript: `
const pluck = <T, K extends keyof T>(arr: T[], key: K): T[K][] => {
  return arr.map(item => item[key]);
};
`,

    example: `
const users = [
  { id: 1, name: "Aman", role: "admin" },
  { id: 2, name: "Ravi", role: "user" }
];

pluck(users, "name") → ["Aman", "Ravi"]
pluck(users, "role") → ["admin", "user"]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Analyst",
    ],

    // 🧠 Difficulty level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies where it’s commonly used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Extracting IDs from API responses",
      "Preparing dropdown/select options",
      "Mapping user names or labels",
      "Analytics & reporting data prep",
      "Normalizing backend responses",
      "Interview array transformation questions",
    ],
  },
  {
    category: "🧠 Array Helpers",
    name: "sortByKey",
    desc: "Sorts an array of objects by a specific key (ascending by default)",

    // ✅ JavaScript (Universal & safe copy)
    javascript: `
const sortByKey = (arr, key) => {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort((a, b) => {
    if (a[key] > b[key]) return 1;
    if (a[key] < b[key]) return -1;
    return 0;
  });
};
`,

    // ✅ TypeScript (Generic & type-safe)
    typescript: `
const sortByKey = <T, K extends keyof T>(arr: T[], key: K): T[] => {
  return [...arr].sort((a, b) => {
    if (a[key] > b[key]) return 1;
    if (a[key] < b[key]) return -1;
    return 0;
  });
};
`,

    example: `
const products = [
  { id: 1, name: "Laptop", price: 900 },
  { id: 2, name: "Mouse", price: 20 },
  { id: 3, name: "Keyboard", price: 50 }
];

sortByKey(products, "price")
→ [
  { name: "Mouse", price: 20 },
  { name: "Keyboard", price: 50 },
  { name: "Laptop", price: 900 }
]
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🧠 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies where it’s heavily used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 📍 Real-world use cases
    useCases: [
      "Sorting products by price, rating, or date",
      "Ordering tables & lists in UI",
      "Admin dashboards & analytics panels",
      "Backend data normalization",
      "Search & filter features",
      "Interview questions on array manipulation",
    ],
  },
  /* ================================
   📦 OBJECT HELPERS
================================ */
  {
    category: "📦 Object Helpers",
    name: "pick",
    desc: "Creates a new object by picking only specific keys from an existing object",

    // ✅ JavaScript (most common usage)
    javascript: `
const pick = (obj, keys) => {
  if (!obj || typeof obj !== "object") return {};
  return keys.reduce((acc, key) => {
    if (key in obj) acc[key] = obj[key];
    return acc;
  }, {});
};
`,

    // ✅ TypeScript (type-safe & interview-friendly)
    typescript: `
const pick = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  return keys.reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {} as Pick<T, K>);
};
`,

    example: `
const user = {
  id: 1,
  name: "Rahul",
  email: "rahul@gmail.com",
  role: "admin",
  age: 28
};

pick(user, ["name", "email"])
→ { name: "Rahul", email: "rahul@gmail.com" }
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "API Engineer",
    ],

    // 🎯 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Preparing API request payloads",
      "Hiding sensitive fields (password, tokens)",
      "Optimizing network responses",
      "Form submissions",
      "Redux / Zustand state shaping",
      "DTO creation in backend",
      "Interview questions on object manipulation",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "omit",
    desc: "Creates a new object by removing specified keys from an existing object",

    // ✅ JavaScript (most common usage)
    javascript: `
const omit = (obj, keys) => {
  if (!obj || typeof obj !== "object") return {};
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key))
  );
};
`,

    // ✅ TypeScript (type-safe & interview-ready)
    typescript: `
const omit = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K))
  ) as Omit<T, K>;
};
`,

    example: `
const user = {
  id: 1,
  name: "Rahul",
  email: "rahul@gmail.com",
  password: "secret123",
  role: "admin"
};

omit(user, ["password"])
→ { id: 1, name: "Rahul", email: "rahul@gmail.com", role: "admin" }
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "API Engineer",
      "Security-focused Developer",
    ],

    // 🎯 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "Node.js",
      "Express",
      "NestJS",
      "React Native",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Removing sensitive fields before sending API response",
      "Cleaning form data before submission",
      "User object sanitization",
      "Backend DTO shaping",
      "Reducing payload size",
      "Security best practices",
      "Interview questions on object manipulation",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "deepClone",
    desc: "Creates a deep copy of an object without shared references",

    // ✅ JavaScript (production-safe)
    javascript: `
const deepClone = (obj) => {
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
};
`,

    // ✅ TypeScript (type-safe)
    typescript: `
const deepClone = <T>(obj: T): T => {
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj)) as T;
};
`,

    example: `
const state = {
  user: { name: "Amit", skills: ["JS", "React"] },
  loggedIn: true
};

const copy = deepClone(state);
copy.user.name = "Rahul";

state.user.name → "Amit"
copy.user.name  → "Rahul"
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "State Management Engineer",
    ],

    // 🎯 Skill level
    levels: ["Beginner", "Intermediate", "Advanced"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux",
      "Zustand",
      "Vue",
      "Angular",
      "Node.js",
      "React Native",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "State immutability in React/Redux",
      "Undo/Redo feature implementation",
      "Form state snapshots",
      "Preventing accidental mutations",
      "Cloning API response safely",
      "Optimistic UI updates",
      "Interview questions on deep vs shallow copy",
    ],

    // ⚠️ Important notes (interview gold)
    caveats: [
      "JSON method breaks Date, Map, Set, functions",
      "structuredClone supports Date, Map, Set, RegExp",
      "Avoid deep cloning very large objects frequently",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "isEmptyObject",
    desc: "Checks whether an object has no own enumerable properties",

    // ✅ JavaScript
    javascript: `
const isEmptyObject = (obj) => {
  return (
    obj !== null &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
};
`,

    // ✅ TypeScript
    typescript: `
const isEmptyObject = (obj: unknown): boolean => {
  return (
    obj !== null &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj as object).length === 0
  );
};
`,

    example: `
isEmptyObject({})            → true
isEmptyObject({ a: 1 })      → false
isEmptyObject([])            → false
isEmptyObject(null)          → false
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
    ],

    // 🎯 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux",
      "React Native",
      "Node.js",
      "Express",
      "Vue",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Form validation (check empty payload)",
      "API request body validation",
      "Conditional UI rendering",
      "State reset checks",
      "Guard clauses in functions",
      "Avoid unnecessary API calls",
    ],

    // ⚠️ Important notes (interview-ready)
    caveats: [
      "Arrays are not considered empty objects",
      "Does not check nested emptiness",
      "Object.create(null) needs special handling",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "invertObject",
    desc: "Swaps object keys and values",

    // ✅ JavaScript
    javascript: `
const invertObject = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [value, key])
  );
};
`,

    // ✅ TypeScript
    typescript: `
const invertObject = <T extends Record<string, string | number>>(
  obj: T
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [String(value), key])
  );
};
`,

    example: `
invertObject({ a: 1, b: 2 })
→ { "1": "a", "2": "b" }

invertObject({ admin: "A", user: "U" })
→ { "A": "admin", "U": "user" }
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
    ],

    // 🎯 Skill level
    levels: ["Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux",
      "Node.js",
      "React Native",
      "Express",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Enum reverse lookup",
      "Mapping IDs to labels",
      "Permission / role mapping",
      "Dropdown value-label inversion",
      "Configuration transformations",
      "Interview object manipulation questions",
    ],

    // ⚠️ Caveats (important for interviews)
    caveats: [
      "Duplicate values will overwrite keys",
      "Values must be string or number",
      "Not suitable for complex objects as values",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "getNested",
    desc: "Safely access deeply nested object values using dot notation",

    // ✅ JavaScript
    javascript: `
const getNested = (obj, path, def = null) =>
  path
    .split(".")
    .reduce((acc, key) => acc?.[key], obj) ?? def;
`,

    // ✅ TypeScript (strongly typed + safe)
    typescript: `
const getNested = <T = any>(
  obj: Record<string, any>,
  path: string,
  def: T | null = null
): T | null => {
  return (
    path
      .split(".")
      .reduce<any>((acc, key) => acc?.[key], obj) ?? def
  );
};
`,

    example: `
const user = {
  profile: {
    name: "Rahul",
    address: {
      city: "Delhi"
    }
  }
};

getNested(user, "profile.name")
→ "Rahul"

getNested(user, "profile.address.city")
→ "Delhi"

getNested(user, "profile.age", 18)
→ 18
`,

    // 👨‍💻 Who uses this
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
    ],

    // 🎯 Skill level
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux",
      "Node.js",
      "Express",
      "React Native",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Safely reading API response data",
      "Avoiding undefined/null crashes",
      "Accessing deeply nested Redux state",
      "Form value extraction",
      "Optional configuration lookup",
      "Replacing lodash.get",
    ],

    // ⚠️ Caveats / Interview points
    caveats: [
      "Path must be a string (dot notation only)",
      "Array index access requires numeric keys (e.g. users.0.name)",
      "Returns default value only if result is null or undefined",
      "Does not create missing paths (read-only helper)",
    ],
  },
  {
    category: "📦 Object Helpers",
    name: "setNested",
    desc: "Safely set a deeply nested value inside an object using dot notation",

    // ✅ JavaScript
    javascript: `
const setNested = (obj, path, value) => {
  const keys = path.split(".");
  let current = obj;

  keys.slice(0, -1).forEach((key) => {
    if (typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  });

  current[keys[keys.length - 1]] = value;
  return obj;
};
`,

    // ✅ TypeScript (safe + typed)
    typescript: `
const setNested = (
  obj: Record<string, any>,
  path: string,
  value: any
): Record<string, any> => {
  const keys = path.split(".");
  let current: any = obj;

  keys.slice(0, -1).forEach((key) => {
    if (typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  });

  current[keys[keys.length - 1]] = value;
  return obj;
};
`,

    example: `
const user = {
  profile: {
    name: "Rahul"
  }
};

setNested(user, "profile.age", 30);
→ { profile: { name: "Rahul", age: 30 } }

setNested(user, "settings.theme.color", "dark");
→ {
  profile: { name: "Rahul", age: 30 },
  settings: { theme: { color: "dark" } }
}
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies where used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux / Zustand",
      "Node.js",
      "Express",
      "React Native",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Updating nested Redux or Zustand state",
      "Building dynamic forms",
      "Normalizing API payloads",
      "Creating objects from dot-notation configs",
      "Avoiding 'cannot set property of undefined' errors",
      "Replacing lodash.set",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Mutates the original object (not immutable)",
      "Not safe for arrays unless path is carefully handled",
      "For immutable updates, clone object before using",
      "Path must be dot-separated string",
    ],

    // 💡 Pro Tip
    proTip:
      "For React state, use setNested(deepClone(state), path, value) to keep immutability",
  },
  {
    category: "📦 Object Helpers",
    name: "mergeDeep",
    desc: "Deeply merges two objects, recursively combining nested properties",

    // ✅ JavaScript
    javascript: `
const mergeDeep = (target, source) => {
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      if (!target[key] || typeof target[key] !== "object") {
        target[key] = {};
      }
      mergeDeep(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
};
`,

    // ✅ TypeScript
    typescript: `
const mergeDeep = (
  target: Record<string, any>,
  source: Record<string, any>
): Record<string, any> => {
  for (const key in source) {
    const value = source[key];

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      if (!target[key] || typeof target[key] !== "object") {
        target[key] = {};
      }
      mergeDeep(target[key], value);
    } else {
      target[key] = value;
    }
  }
  return target;
};
`,

    example: `
const config = {
  api: {
    timeout: 3000,
    retries: 2,
  },
  theme: {
    color: "blue",
  },
};

const overrides = {
  api: {
    retries: 5,
  },
  theme: {
    darkMode: true,
  },
};

mergeDeep(config, overrides);

→ {
  api: { timeout: 3000, retries: 5 },
  theme: { color: "blue", darkMode: true }
}
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "DevOps Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Intermediate", "Advanced"],

    // 🛠️ Technologies where used
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Redux",
      "Node.js",
      "Express",
      "Configuration systems",
      "Server-side rendering",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Merging default config with environment overrides",
      "Combining API request options",
      "Theme + user preference merging",
      "Server config layering (base → env → runtime)",
      "Replacing lodash.merge in small projects",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Mutates the target object (not immutable)",
      "Arrays are overwritten, not merged",
      "Does not handle circular references",
      "For immutability, clone target before merge",
    ],

    // 💡 Pro Tip
    proTip:
      "In React state updates, always call mergeDeep(deepClone(state), updates) to avoid mutation bugs",
  },
  {
    category: "📦 Object Helpers",
    name: "objectSize",
    desc: "Returns the number of own enumerable keys in an object",

    // ✅ JavaScript
    javascript: `
const objectSize = (obj) =>
  obj && typeof obj === "object"
    ? Object.keys(obj).length
    : 0;
`,

    // ✅ TypeScript
    typescript: `
const objectSize = (obj: Record<string, unknown> | null): number => {
  if (!obj || typeof obj !== "object") return 0;
  return Object.keys(obj).length;
};
`,

    example: `
objectSize({ a: 1, b: 2 }) → 2
objectSize({}) → 0
objectSize(null) → 0
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "React Developer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Node.js",
      "Express",
      "Redux",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Check if API response object is empty",
      "Conditional UI rendering (empty state vs data)",
      "Form validation (at least one field filled)",
      "Redux / Zustand state checks",
      "Guard clauses in backend controllers",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Counts only own enumerable properties",
      "Does not count symbol keys",
      "Returns 0 for non-object values",
      "Arrays will return their index count",
    ],

    // 💡 Pro Tip
    proTip:
      "For safe empty checks in real apps, combine with isEmptyObject or check Object.keys(obj).length === 0 explicitly",
  },
  {
    category: "📦 Object Helpers",
    name: "hasKeys",
    desc: "Checks whether all required keys exist in an object",

    // ✅ JavaScript
    javascript: `
const hasKeys = (obj, keys = []) =>
  obj != null &&
  typeof obj === "object" &&
  keys.every(key => key in obj);
`,

    // ✅ TypeScript
    typescript: `
const hasKeys = (
  obj: Record<string, unknown> | null,
  keys: string[]
): boolean => {
  if (!obj || typeof obj !== "object") return false;
  return keys.every(key => key in obj);
};
`,

    example: `
hasKeys({ id: 1, email: "a@test.com" }, ["id", "email"]) → true
hasKeys({ id: 1 }, ["id", "email"]) → false
hasKeys(null, ["id"]) → false
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "React Developer",
      "API Developer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Node.js",
      "Express",
      "NestJS",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Validate API response structure",
      "Guard clauses before rendering UI",
      "Form submission validation",
      "Checking required fields in backend controllers",
      "Avoid runtime errors due to missing properties",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "`in` checks inherited properties as well",
      "Does not validate value type or emptiness",
      "Returns true even if value is undefined",
    ],

    // 💡 Pro Tip
    proTip:
      "For strict checks, combine hasKeys with value validation (e.g. obj[key] != null) instead of relying only on key existence",
  },
  /* ================================
   ⏰ DATE & TIME HELPERS
================================ */ {
    category: "⏰ Date & Time",
    name: "formatDate",
    desc: "Formats date as DD Mon YYYY",
    code: `const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });`,
    example: `formatDate("2025-01-01") → "01 Jan 2025"`,
  },
  {
    category: "⏰ Date & Time",
    name: "timeAgo",
    desc: "Returns relative time like '2 hours ago'",
    code: `const timeAgo = (date) => {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (let [u, s] of units) {
    const v = Math.floor(diff / s);
    if (v >= 1) return \`\${v} \${u}\${v>1?"s":""} ago\`;
  }
  return "Just now";
};`,
    example: `timeAgo(Date.now() - 3600000) → "1 hour ago"`,
  },
  {
    category: "⏰ Date & Time",
    name: "isToday",
    desc: "Checks if date is today",
    code: `const isToday = (date) =>
  new Date(date).toDateString() === new Date().toDateString();`,
    example: `isToday(new Date()) → true`,
  },
  {
    category: "⏰ Date & Time",
    name: "isPast",
    desc: "Checks if date is in the past",
    code: `const isPast = (date) =>
  new Date(date) < new Date();`,
    example: `isPast("2020-01-01") → true`,
  },
  {
    category: "⏰ Date & Time",
    name: "isFuture",
    desc: "Checks if date is in the future",
    code: `const isFuture = (date) =>
  new Date(date) > new Date();`,
    example: `isFuture("2030-01-01") → true`,
  },
  {
    category: "⏰ Date & Time",
    name: "daysBetween",
    desc: "Returns days between two dates",
    code: `const daysBetween = (d1, d2) =>
  Math.ceil(
    Math.abs(new Date(d2) - new Date(d1)) /
    (1000 * 60 * 60 * 24)
  );`,
    example: `daysBetween("2024-01-01","2024-01-10") → 9`,
  },
  {
    category: "⏰ Date & Time",
    name: "addDays",
    desc: "Adds days to a date",
    code: `const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};`,
    example: `addDays(new Date(), 7)`,
  },
  {
    category: "⏰ Date & Time",
    name: "startOfWeek",
    desc: "Returns Monday of current week",
    code: `const startOfWeek = (date) => {
  const d = new Date(date);
  const diff = d.getDate() - d.getDay() + 1;
  return new Date(d.setDate(diff));
};`,
    example: `startOfWeek(new Date())`,
  },
  {
    category: "⏰ Date & Time",
    name: "endOfMonth",
    desc: "Returns last day of month",
    code: `const endOfMonth = (date) =>
  new Date(
    new Date(date).getFullYear(),
    new Date(date).getMonth() + 1,
    0
  );`,
    example: `endOfMonth(new Date())`,
  },
  {
    category: "⏰ Date & Time",
    name: "getDayLabel",
    desc: "Returns day label (Mon, Tue)",
    code: `const getDayLabel = (date) =>
  new Date(date).toLocaleString("en", {
    weekday: "short",
  });`,
    example: `getDayLabel(new Date()) → "Mon"`,
  },
  /* ================================
   💰 PRICE & NUMBER HELPERS
================================ */

  {
    category: "💰 Price & Numbers",
    name: "formatCurrency",
    desc: "Formats a number into a localized currency string (₹, $, AED, etc.)",

    // ✅ JavaScript
    javascript: `
const formatCurrency = (amount, currency = "INR", locale = "en-IN") => {
  if (typeof amount !== "number") return "";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};
`,

    // ✅ TypeScript
    typescript: `
const formatCurrency = (
  amount: number,
  currency: string = "INR",
  locale: string = "en-IN"
): string => {
  if (typeof amount !== "number") return "";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};
`,

    example: `
formatCurrency(12999) → "₹12,999"
formatCurrency(12999, "USD", "en-US") → "$12,999"
formatCurrency(12999, "AED", "en-AE") → "AED 12,999"
formatCurrency(999.99, "EUR", "de-DE") → "1.000 €"
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Fintech Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Node.js",
      "React Native",
      "Expo",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "E-commerce product pricing",
      "Subscription plans & billing UI",
      "Fintech dashboards",
      "Invoices & receipts",
      "Analytics & revenue reporting",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Relies on browser Intl API (supported in modern environments)",
      "Locale impacts formatting (comma vs dot separators)",
      "maximumFractionDigits may need adjustment for crypto or taxes",
    ],

    // 💡 Pro Tips
    proTip:
      "Always pass locale explicitly for global apps; never assume 'en-IN' or 'en-US' when dealing with international users.",
  },
  {
    category: "💰 Price & Numbers",
    name: "formatCompactNumber",
    desc: "Converts large numbers into compact human-readable format like 1K, 1.2M, 3.4B",

    // ✅ JavaScript
    javascript: `
const formatCompactNumber = (num, locale = "en") => {
  if (typeof num !== "number") return "";
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
  }).format(num);
};
`,

    // ✅ TypeScript
    typescript: `
const formatCompactNumber = (
  num: number,
  locale: string = "en"
): string => {
  if (typeof num !== "number") return "";
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
  }).format(num);
};
`,

    example: `
formatCompactNumber(950) → "950"
formatCompactNumber(1250) → "1.3K"
formatCompactNumber(1250000) → "1.3M"
formatCompactNumber(987654321) → "988M"
formatCompactNumber(1200000000) → "1.2B"
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Visualization Engineer",
      "Analytics Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "React Native",
      "Flutter (logic transferable)",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Social media follower counts",
      "Dashboard KPIs",
      "Analytics charts",
      "E-commerce sales metrics",
      "Admin panels",
      "Mobile app stats (likes, views)",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Uses Intl API (modern browsers & Node.js supported)",
      "Rounding may differ by locale",
      "Not ideal for financial precision (display-only)",
    ],

    // 💡 Pro Tips
    proTip:
      "Use compact numbers only for UI display. For calculations, always store and process raw numeric values.",
  },
  {
    category: "💰 Price & Numbers",
    name: "formatPercentage",
    desc: "Formats a decimal value into a human-readable percentage string",

    // ✅ JavaScript
    javascript: `
const formatPercentage = (value, decimals = 0) => {
  if (typeof value !== "number") return "";
  return \`\${(value * 100).toFixed(decimals)}%\`;
};
`,

    // ✅ TypeScript
    typescript: `
const formatPercentage = (
  value: number,
  decimals: number = 0
): string => {
  if (typeof value !== "number") return "";
  return \`\${(value * 100).toFixed(decimals)}%\`;
};
`,

    example: `
formatPercentage(0.1) → "10%"
formatPercentage(0.256) → "26%"
formatPercentage(0.256, 1) → "25.6%"
formatPercentage(1) → "100%"
formatPercentage(0) → "0%"
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Analyst",
      "Product Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "React Native",
      "Node.js",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Progress bars",
      "Completion indicators",
      "Discount displays",
      "Analytics dashboards",
      "Test scores & ratings",
      "Conversion rate display",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Expects decimal input (0–1 range)",
      "Not suitable if backend already returns percentage",
      "Rounding depends on decimals argument",
    ],

    // 💡 Pro Tips
    proTip:
      "Always clarify whether your API returns percentage (25) or ratio (0.25) to avoid double conversion bugs.",
  },
  {
    category: "💰 Price & Numbers",
    name: "roundToDecimals",
    desc: "Rounds a number to a fixed number of decimal places safely",

    // ✅ JavaScript
    javascript: `
const roundToDecimals = (num, decimals = 2) => {
  if (typeof num !== "number") return 0;
  return Number(Math.round(num + "e" + decimals) + "e-" + decimals);
};
`,

    // ✅ TypeScript
    typescript: `
const roundToDecimals = (
  num: number,
  decimals: number = 2
): number => {
  if (typeof num !== "number") return 0;
  return Number(Math.round(num + "e" + decimals) + "e-" + decimals);
};
`,

    example: `
roundToDecimals(3.14159) → 3.14
roundToDecimals(3.14159, 3) → 3.142
roundToDecimals(1.005, 2) → 1.01
roundToDecimals(10, 2) → 10
roundToDecimals(0.333333, 4) → 0.3333
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Data Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "React Native",
      "Node.js",
      "Express",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Price rounding in e-commerce",
      "Financial calculations",
      "Analytics dashboards",
      "Chart & graph values",
      "Tax & discount calculations",
      "Score & rating precision",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Avoid using toFixed() if you need a number (it returns string)",
      "Floating-point precision issues are common in JS",
      "Use this method instead of Math.round(num * 100) / 100",
    ],

    // 💡 Pro Tips
    proTip:
      "This scientific-notation trick (`e`) is the most reliable way to handle floating-point rounding in JavaScript interviews and production code.",
  },
  {
    category: "💰 Price & Numbers",
    name: "clampNumber",
    desc: "Restricts a number so it never goes below min or above max",

    // ✅ JavaScript
    javascript: `
const clampNumber = (num, min, max) => {
  if (typeof num !== "number") return min;
  return Math.min(Math.max(num, min), max);
};
`,

    // ✅ TypeScript
    typescript: `
const clampNumber = (
  num: number,
  min: number,
  max: number
): number => {
  return Math.min(Math.max(num, min), max);
};
`,

    example: `
clampNumber(120, 0, 100) → 100
clampNumber(-10, 0, 100) → 0
clampNumber(50, 0, 100) → 50
clampNumber(1.5, 0, 1) → 1
clampNumber(0.4, 0, 1) → 0.4
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Game Developer",
      "Data Engineer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner", "Intermediate"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Next.js",
      "Vue",
      "Angular",
      "React Native",
      "Node.js",
      "Express",
      "D3.js",
      "Three.js",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Form input validation (min/max values)",
      "Price sliders (range inputs)",
      "Volume / brightness controls",
      "Progress bars & percentages",
      "Game physics (speed, health limits)",
      "Chart & animation boundaries",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Always validate min <= max before clamping",
      "Works for integers and floats",
      "Does not mutate original value",
    ],

    // 💡 Pro Tips
    proTip:
      "Clamp is a must-know helper for UI sliders, animations, and game logic. Interviewers often expect this one-liner instantly.",
  },
  {
    category: "💰 Price & Numbers",
    name: "isEven",
    desc: "Checks whether a number is even (divisible by 2)",

    // ✅ JavaScript
    javascript: `
const isEven = (num) => {
  if (typeof num !== "number") return false;
  return num % 2 === 0;
};
`,

    // ✅ TypeScript
    typescript: `
const isEven = (num: number): boolean => {
  return num % 2 === 0;
};
`,

    example: `
isEven(4) → true
isEven(7) → false
isEven(0) → true
isEven(-2) → true
isEven(3.5) → false
`,

    // 👨‍💻 Developer roles
    roles: [
      "Frontend Developer",
      "Backend Developer",
      "Full Stack Developer",
      "Mobile App Developer",
      "Game Developer",
      "Competitive Programmer",
    ],

    // 🎯 Experience levels
    levels: ["Beginner"],

    // 🛠️ Technologies
    technologies: [
      "JavaScript",
      "TypeScript",
      "React",
      "Vue",
      "Angular",
      "Node.js",
      "React Native",
      "Express",
      "NestJS",
    ],

    // 🌍 Real-world use cases
    useCases: [
      "Alternating UI layouts (odd/even rows)",
      "Table row styling (zebra striping)",
      "Pagination logic",
      "Game logic (turn-based systems)",
      "Data validation rules",
      "Interview coding rounds (basic logic)",
    ],

    // ⚠️ Caveats / Interview notes
    caveats: [
      "Works correctly only for integers",
      "Decimals should be validated separately",
      "Negative even numbers are still even",
    ],

    // 💡 Pro Tips
    proTip:
      "This looks simple, but it’s often used inside loops for UI patterns (odd/even rows). Interviewers expect zero hesitation on this one.",
  },
  {
    category: "💰 Price & Numbers",
    name: "isOdd",
    desc: "Checks if number is odd",
    code: `const isOdd = (num) =>
  num % 2 !== 0;`,
    example: `isOdd(7) → true`,
  },
  {
    category: "💰 Price & Numbers",
    name: "sum",
    desc: "Returns sum of number array",
    code: `const sum = (arr) =>
  arr.reduce((a, b) => a + b, 0);`,
    example: `sum([1,2,3]) → 6`,
  },
  {
    category: "💰 Price & Numbers",
    name: "average",
    desc: "Returns average of numbers",
    code: `const average = (arr) =>
  sum(arr) / arr.length;`,
    example: `average([10,20,30]) → 20`,
  },
  {
    category: "💰 Price & Numbers",
    name: "randomInRange",
    desc: "Generates random number between range",
    code: `const randomInRange = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;`,
    example: `randomInRange(1,10) → 7`,
  } /* ================================
   📝 TEXT & UI HELPERS
================================ */,

  {
    category: "📝 Text & UI",
    name: "truncateText",
    desc: "Truncates long text with ellipsis",
    code: `const truncateText = (text, length = 50) =>
  text?.length > length ? text.slice(0, length) + "…" : text;`,
    example: `truncateText("Hello World", 5) → "Hello…"`,
  },
  {
    category: "📝 Text & UI",
    name: "capitalize",
    desc: "Capitalizes first letter of string",
    code: `const capitalize = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1);`,
    example: `capitalize("hello") → "Hello"`,
  },
  {
    category: "📝 Text & UI",
    name: "capitalizeWords",
    desc: "Capitalizes every word",
    code: `const capitalizeWords = (str) =>
  str.replace(/\\b\\w/g, (c) => c.toUpperCase());`,
    example: `capitalizeWords("hello world") → "Hello World"`,
  },
  {
    category: "📝 Text & UI",
    name: "toTitleCase",
    desc: "Converts string to title case",
    code: `const toTitleCase = (str) =>
  str.toLowerCase().replace(/\\b\\w/g, (c) => c.toUpperCase());`,
    example: `toTitleCase("hello WORLD") → "Hello World"`,
  },
  {
    category: "📝 Text & UI",
    name: "slugify",
    desc: "Creates URL-friendly slug",
    code: `const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^\\w\\s-]/g, "")
    .replace(/[\\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");`,
    example: `slugify("Hello World 2025") → "hello-world-2025"`,
  },
  {
    category: "📝 Text & UI",
    name: "stripHtml",
    desc: "Removes HTML tags from string",
    code: `const stripHtml = (html) =>
  html.replace(/<[^>]*>/g, "");`,
    example: `stripHtml("<p>Hello</p>") → "Hello"`,
  },
  {
    category: "📝 Text & UI",
    name: "wordCount",
    desc: "Counts words in string",
    code: `const wordCount = (str) =>
  str.trim().split(/\\s+/).length;`,
    example: `wordCount("hello world from js") → 4`,
  },
  {
    category: "📝 Text & UI",
    name: "reverseString",
    desc: "Reverses a string",
    code: `const reverseString = (str) =>
  str.split("").reverse().join("");`,
    example: `reverseString("hello") → "olleh"`,
  },
  {
    category: "📝 Text & UI",
    name: "maskEmail",
    desc: "Masks email for privacy",
    code: `const maskEmail = (email) =>
  email.replace(/(.{2}).+(@.+)/, "$1***$2");`,
    example: `maskEmail("john@gmail.com") → "jo***@gmail.com"`,
  },
  {
    category: "📝 Text & UI",
    name: "maskPhone",
    desc: "Masks phone number",
    code: `const maskPhone = (phone) =>
  phone.replace(/\\d(?=\\d{2})/g, "*");`,
    example: `maskPhone("9876543210") → "********10"`,
  } /* ================================
   📊 DATA PRESENTATION HELPERS
================================ */,

  {
    category: "📊 Data Presentation",
    name: "formatName",
    desc: "Formats full name properly",
    code: `const formatName = (first, last) =>
  [first, last].filter(Boolean).join(" ");`,
    example: `formatName("John", "Doe") → "John Doe"`,
  },
  {
    category: "📊 Data Presentation",
    name: "formatPercentage",
    desc: "Formats number as percentage",
    code: `const formatPercentage = (value, decimals = 0) =>
  \`\${(value * 100).toFixed(decimals)}%\`;`,
    example: `formatPercentage(0.256, 1) → "25.6%"`,
  },
  {
    category: "📊 Data Presentation",
    name: "formatCurrency",
    desc: "Formats number into currency",
    code: `const formatCurrency = (amount, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);`,
    example: `formatCurrency(12999) → "₹12,999"`,
  },
  {
    category: "📊 Data Presentation",
    name: "formatCompactNumber",
    desc: "Formats number to compact form",
    code: `const formatCompactNumber = (num) =>
  new Intl.NumberFormat("en", { notation: "compact" }).format(num);`,
    example: `formatCompactNumber(1250000) → "1.3M"`,
  },
  {
    category: "📊 Data Presentation",
    name: "clampNumber",
    desc: "Restricts number between min and max",
    code: `const clampNumber = (num, min, max) =>
  Math.min(Math.max(num, min), max);`,
    example: `clampNumber(120, 0, 100) → 100`,
  },
  {
    category: "📊 Data Presentation",
    name: "roundToDecimals",
    desc: "Rounds number to fixed decimals",
    code: `const roundToDecimals = (num, decimals = 2) =>
  Number(Math.round(num + "e" + decimals) + "e-" + decimals);`,
    example: `roundToDecimals(3.14159, 2) → 3.14`,
  },
  {
    category: "📊 Data Presentation",
    name: "formatBoolean",
    desc: "Formats boolean to Yes/No",
    code: `const formatBoolean = (val) =>
  val ? "Yes" : "No";`,
    example: `formatBoolean(true) → "Yes"`,
  },
  {
    category: "📊 Data Presentation",
    name: "emptyFallback",
    desc: "Shows fallback text for empty values",
    code: `const emptyFallback = (val, fallback = "-") =>
  val == null || val === "" ? fallback : val;`,
    example: `emptyFallback("", "N/A") → "N/A"`,
  },
  {
    category: "📊 Data Presentation",
    name: "formatList",
    desc: "Formats array into readable string",
    code: `const formatList = (arr) =>
  arr?.length ? arr.join(", ") : "-";`,
    example: `formatList(["JS", "React"]) → "JS, React"`,
  },
  {
    category: "📊 Data Presentation",
    name: "highlightMatch",
    desc: "Highlights matching text (UI search)",
    code: `const highlightMatch = (text, query) =>
  text.replace(
    new RegExp(query, "gi"),
    (match) => \`**\${match}**\`
  );`,
    example: `highlightMatch("Hello World", "world") → "Hello **World**"`,
  } /* ================================
   📅 DATE RANGE & CALENDAR HELPERS
================================ */,

  {
    category: "📅 Date & Calendar",
    name: "getWeekDays",
    desc: "Returns array of week day labels",
    code: `const getWeekDays = () => 
  ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];`,
    example: `getWeekDays() → ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]`,
  },
  {
    category: "📅 Date & Calendar",
    name: "getMonthDays",
    desc: "Returns number of days in a month",
    code: `const getMonthDays = (year, month) =>
  new Date(year, month + 1, 0).getDate();`,
    example: `getMonthDays(2025, 1) → 28`,
  },
  {
    category: "📅 Date & Calendar",
    name: "getMonthName",
    desc: "Returns full month name",
    code: `const getMonthName = (monthIndex) =>
  new Date(0, monthIndex).toLocaleString("en", {
    month: "long",
  });`,
    example: `getMonthName(0) → "January"`,
  },
  {
    category: "📅 Date & Calendar",
    name: "getDayLabel",
    desc: "Returns short weekday label",
    code: `const getDayLabel = (date) =>
  new Date(date).toLocaleString("en", {
    weekday: "short",
  });`,
    example: `getDayLabel("2025-01-01") → "Wed"`,
  },
  {
    category: "📅 Date & Calendar",
    name: "isWeekend",
    desc: "Checks if date is weekend",
    code: `const isWeekend = (date) =>
  [0, 6].includes(new Date(date).getDay());`,
    example: `isWeekend("2025-01-05") → true`,
  },
  {
    category: "📅 Date & Calendar",
    name: "addDays",
    desc: "Adds days to date",
    code: `const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};`,
    example: `addDays("2025-01-01", 5) → Jan 6, 2025`,
  },
  {
    category: "📅 Date & Calendar",
    name: "startOfWeek",
    desc: "Returns start date of week (Sunday)",
    code: `const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
};`,
    example: `startOfWeek("2025-01-08") → Jan 5, 2025`,
  },
  {
    category: "📅 Date & Calendar",
    name: "endOfWeek",
    desc: "Returns end date of week (Saturday)",
    code: `const endOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d;
};`,
    example: `endOfWeek("2025-01-08") → Jan 11, 2025`,
  },
  {
    category: "📅 Date & Calendar",
    name: "startOfMonth",
    desc: "Returns first day of month",
    code: `const startOfMonth = (date) =>
  new Date(new Date(date).getFullYear(),
    new Date(date).getMonth(), 1);`,
    example: `startOfMonth("2025-03-15") → Mar 1, 2025`,
  },
  {
    category: "📅 Date & Calendar",
    name: "endOfMonth",
    desc: "Returns last day of month",
    code: `const endOfMonth = (date) =>
  new Date(new Date(date).getFullYear(),
    new Date(date).getMonth() + 1, 0);`,
    example: `endOfMonth("2025-03-15") → Mar 31, 2025`,
  },
  /* ================================
   🔐 VALIDATION HELPERS
================================ */

  {
    category: "🔐 Validation",
    name: "isValidEmail",
    desc: "Checks if email is valid",
    code: `const isValidEmail = (email) =>
  /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);`,
    example: `isValidEmail("test@mail.com") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isStrongPassword",
    desc: "Checks password strength",
    code: `const isStrongPassword = (pwd) =>
  /^(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&]).{8,}$/.test(pwd);`,
    example: `isStrongPassword("Abc@1234") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isEmpty",
    desc: "Checks if value is empty",
    code: `const isEmpty = (val) =>
  val == null ||
  (typeof val === "string" && val.trim() === "") ||
  (Array.isArray(val) && val.length === 0) ||
  (typeof val === "object" && Object.keys(val).length === 0);`,
    example: `isEmpty("") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isNumeric",
    desc: "Checks if value is numeric",
    code: `const isNumeric = (val) =>
  !isNaN(val) && val !== "";`,
    example: `isNumeric("123") → true`,
  },
  {
    category: "🔐 Validation",
    name: "hasMinLength",
    desc: "Checks minimum string length",
    code: `const hasMinLength = (str, len) =>
  typeof str === "string" && str.length >= len;`,
    example: `hasMinLength("hello", 3) → true`,
  },
  {
    category: "🔐 Validation",
    name: "isValidUrl",
    desc: "Checks if URL is valid",
    code: `const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};`,
    example: `isValidUrl("https://google.com") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isPhoneNumber",
    desc: "Validates phone number (10 digits)",
    code: `const isPhoneNumber = (phone) =>
  /^\\d{10}$/.test(phone);`,
    example: `isPhoneNumber("9876543210") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isAlpha",
    desc: "Checks if string contains only letters",
    code: `const isAlpha = (str) =>
  /^[a-zA-Z]+$/.test(str);`,
    example: `isAlpha("Hello") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isAlphaNumeric",
    desc: "Checks if string is alphanumeric",
    code: `const isAlphaNumeric = (str) =>
  /^[a-zA-Z0-9]+$/.test(str);`,
    example: `isAlphaNumeric("Hello123") → true`,
  },
  {
    category: "🔐 Validation",
    name: "isValidDate",
    desc: "Checks if date is valid",
    code: `const isValidDate = (date) =>
  date instanceof Date && !isNaN(date);`,
    example: `isValidDate(new Date()) → true`,
  } /* ================================
   🧠 ARRAY & OBJECT HELPERS
================================ */,

  {
    category: "🧠 Array & Object",
    name: "uniqueBy",
    desc: "Removes duplicates from array using key",
    code: `const uniqueBy = (arr, key) =>
  [...new Map(arr.map(item => [item[key], item])).values()];`,
    example: `uniqueBy(users, "id")`,
  },
  {
    category: "🧠 Array & Object",
    name: "sortByKey",
    desc: "Sorts array of objects by key",
    code: `const sortByKey = (arr, key) =>
  [...arr].sort((a, b) => a[key] > b[key] ? 1 : -1);`,
    example: `sortByKey(products, "price")`,
  },
  {
    category: "🧠 Array & Object",
    name: "deepClone",
    desc: "Creates deep clone of object",
    code: `const deepClone = (obj) =>
  JSON.parse(JSON.stringify(obj));`,
    example: `deepClone(user)`,
  },
  {
    category: "🧠 Array & Object",
    name: "isEqual",
    desc: "Checks deep equality between objects",
    code: `const isEqual = (a, b) =>
  JSON.stringify(a) === JSON.stringify(b);`,
    example: `isEqual(obj1, obj2) → true`,
  },
  {
    category: "🧠 Array & Object",
    name: "pick",
    desc: "Picks selected keys from object",
    code: `const pick = (obj, keys) =>
  keys.reduce((acc, k) => ({ ...acc, [k]: obj[k] }), {});`,
    example: `pick(user, ["name", "email"])`,
  },
  {
    category: "🧠 Array & Object",
    name: "omit",
    desc: "Removes selected keys from object",
    code: `const omit = (obj, keys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([k]) => !keys.includes(k))
  );`,
    example: `omit(user, ["password"])`,
  },
  {
    category: "🧠 Array & Object",
    name: "chunkArray",
    desc: "Splits array into chunks",
    code: `const chunkArray = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) },
    (_, i) => arr.slice(i * size, i * size + size)
  );`,
    example: `chunkArray([1,2,3,4], 2) → [[1,2],[3,4]]`,
  },
  {
    category: "🧠 Array & Object",
    name: "flattenArray",
    desc: "Flattens nested arrays",
    code: `const flattenArray = (arr) =>
  arr.flat(Infinity);`,
    example: `flattenArray([1,[2,[3]]]) → [1,2,3]`,
  },
  {
    category: "🧠 Array & Object",
    name: "groupBy",
    desc: "Groups array by key",
    code: `const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    acc[item[key]] = acc[item[key]] || [];
    acc[item[key]].push(item);
    return acc;
  }, {});`,
    example: `groupBy(users, "role")`,
  },
  {
    category: "🧠 Array & Object",
    name: "compactArray",
    desc: "Removes falsy values from array",
    code: `const compactArray = (arr) =>
  arr.filter(Boolean);`,
    example: `compactArray([0,1,false,2]) → [1,2]`,
  } /* ================================
   🌐 API / NETWORK HELPERS
================================ */,

  {
    category: "🌐 API / Network",
    name: "buildQueryParams",
    desc: "Builds query string from object",
    code: `const buildQueryParams = (params) =>
  new URLSearchParams(params).toString();`,
    example: `buildQueryParams({ page: 1, limit: 10 }) → "page=1&limit=10"`,
  },
  {
    category: "🌐 API / Network",
    name: "safeJSONParse",
    desc: "Safely parses JSON without crashing",
    code: `const safeJSONParse = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};`,
    example: `safeJSONParse('invalid', {}) → {}`,
  },
  {
    category: "🌐 API / Network",
    name: "withRetry",
    desc: "Retries async function on failure",
    code: `const withRetry = async (fn, retries = 3) => {
  try {
    return await fn();
  } catch (err) {
    if (retries <= 1) throw err;
    return withRetry(fn, retries - 1);
  }
};`,
    example: `withRetry(fetchData, 3)`,
  },
  {
    category: "🌐 API / Network",
    name: "timeoutPromise",
    desc: "Rejects promise if it exceeds timeout",
    code: `const timeoutPromise = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject("Timeout"), ms)
    )
  ]);`,
    example: `timeoutPromise(fetch(url), 5000)`,
  },
  {
    category: "🌐 API / Network",
    name: "debounce",
    desc: "Debounces function execution",
    code: `const debounce = (fn, delay = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};`,
    example: `onSearch = debounce(searchAPI, 500)`,
  },
  {
    category: "🌐 API / Network",
    name: "throttle",
    desc: "Throttles function calls",
    code: `const throttle = (fn, delay = 300) => {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last > delay) {
      last = now;
      fn(...args);
    }
  };
};`,
    example: `onScroll = throttle(handleScroll, 200)`,
  },
  {
    category: "🌐 API / Network",
    name: "fetchJSON",
    desc: "Fetch wrapper with JSON response",
    code: `const fetchJSON = async (url, options = {}) => {
  const res = await fetch(url, options);
  if (!res.ok) throw new Error("Request failed");
  return res.json();
};`,
    example: `fetchJSON("/api/users")`,
  },
  {
    category: "🌐 API / Network",
    name: "abortableFetch",
    desc: "Fetch with abort controller",
    code: `const abortableFetch = (url) => {
  const controller = new AbortController();
  return {
    promise: fetch(url, { signal: controller.signal }),
    abort: () => controller.abort()
  };
};`,
    example: `const { abort } = abortableFetch(url)`,
  },
  {
    category: "🌐 API / Network",
    name: "parseQueryParams",
    desc: "Parses query params from URL",
    code: `const parseQueryParams = (url) =>
  Object.fromEntries(
    new URL(url).searchParams.entries()
  );`,
    example: `parseQueryParams(location.href)`,
  },
  {
    category: "🌐 API / Network",
    name: "isOnline",
    desc: "Checks network connectivity",
    code: `const isOnline = () =>
  typeof navigator !== "undefined" && navigator.onLine;`,
    example: `isOnline() → true / false`,
  } /* ================================
   🧪 TESTING & DEBUG HELPERS
================================ */,

  {
    category: "🧪 Testing & Debug",
    name: "mockDelay",
    desc: "Simulates network delay",
    code: `const mockDelay = (ms = 500) =>
  new Promise((resolve) => setTimeout(resolve, ms));`,
    example: `await mockDelay(1000)`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "fakeApiResponse",
    desc: "Mocks API response with delay",
    code: `const fakeApiResponse = (data, delay = 800) =>
  new Promise((resolve) =>
    setTimeout(() => resolve({ success: true, data }), delay)
  );`,
    example: `fakeApiResponse({ users: [] })`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "logWithTime",
    desc: "Logs messages with timestamp",
    code: `const logWithTime = (...args) =>
  console.log(
    \`[\${new Date().toLocaleTimeString()}]\`,
    ...args
  );`,
    example: `logWithTime("API called")`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "measureExecutionTime",
    desc: "Measures function execution time",
    code: `const measureExecutionTime = (fn) => {
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  console.log(\`Execution: \${(end - start).toFixed(2)}ms\`);
  return result;
};`,
    example: `measureExecutionTime(() => heavyTask())`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "warnOnce",
    desc: "Logs warning only once",
    code: `const warnOnce = (() => {
  const shown = new Set();
  return (msg) => {
    if (!shown.has(msg)) {
      console.warn(msg);
      shown.add(msg);
    }
  };
})();`,
    example: `warnOnce("Deprecated API")`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "assert",
    desc: "Simple assertion helper",
    code: `const assert = (condition, message) => {
  if (!condition) {
    throw new Error(message || "Assertion failed");
  }
};`,
    example: `assert(user !== null, "User missing")`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "trace",
    desc: "Logs stack trace",
    code: `const trace = (label = "Trace") =>
  console.trace(label);`,
    example: `trace("Unexpected state")`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "noop",
    desc: "No operation function",
    code: `const noop = () => {};`,
    example: `onClick={noop}`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "freezeObject",
    desc: "Prevents object mutation",
    code: `const freezeObject = (obj) =>
  Object.freeze(obj);`,
    example: `freezeObject(config)`,
  },
  {
    category: "🧪 Testing & Debug",
    name: "deepFreeze",
    desc: "Recursively freezes object",
    code: `const deepFreeze = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (
      typeof obj[key] === "object" &&
      obj[key] !== null
    ) {
      deepFreeze(obj[key]);
    }
  });
  return Object.freeze(obj);
};`,
    example: `deepFreeze(state)`,
  } /* ================================
   🎨 UI / CSS / DOM HELPERS
================================ */,

  {
    category: "🎨 UI / CSS / DOM",
    name: "classNames",
    desc: "Conditionally joins class names",
    code: `const classNames = (...classes) =>
  classes.filter(Boolean).join(" ");`,
    example: `classNames("btn", isActive && "active")`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "toggleBodyScroll",
    desc: "Enable/disable body scroll (modals)",
    code: `const toggleBodyScroll = (lock = true) => {
  document.body.style.overflow = lock ? "hidden" : "auto";
};`,
    example: `toggleBodyScroll(true)`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "scrollToTop",
    desc: "Smooth scroll to top",
    code: `const scrollToTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });`,
    example: `scrollToTop()`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "scrollToElement",
    desc: "Scrolls to DOM element smoothly",
    code: `const scrollToElement = (id) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth",
  });
};`,
    example: `scrollToElement("section-3")`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "getScrollPercent",
    desc: "Returns page scroll percentage",
    code: `const getScrollPercent = () => {
  const scrolled = window.scrollY;
  const height =
    document.documentElement.scrollHeight -
    document.documentElement.clientHeight;
  return Math.round((scrolled / height) * 100);
};`,
    example: `getScrollPercent() → 42`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "isInViewport",
    desc: "Checks if element is in viewport",
    code: `const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};`,
    example: `isInViewport(ref.current)`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "copyToClipboard",
    desc: "Copies text to clipboard",
    code: `const copyToClipboard = (text) =>
  navigator.clipboard.writeText(text);`,
    example: `copyToClipboard("Hello World")`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "getCSSVariable",
    desc: "Reads CSS variable value",
    code: `const getCSSVariable = (name) =>
  getComputedStyle(document.documentElement)
    .getPropertyValue(name);`,
    example: `getCSSVariable("--primary-color")`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "setCSSVariable",
    desc: "Updates CSS variable",
    code: `const setCSSVariable = (name, value) =>
  document.documentElement.style
    .setProperty(name, value);`,
    example: `setCSSVariable("--primary-color", "#3b82f6")`,
  },
  {
    category: "🎨 UI / CSS / DOM",
    name: "detectTheme",
    desc: "Detects system dark/light mode",
    code: `const detectTheme = () =>
  window.matchMedia("(prefers-color-scheme: dark)")
    .matches
    ? "dark"
    : "light";`,
    example: `detectTheme() → "dark"`,
  } /* ================================
   📦 STORAGE HELPERS
================================ */,

  {
    category: "📦 Storage",
    name: "setLocal",
    desc: "Save value to localStorage",
    code: `const setLocal = (key, value) =>
  localStorage.setItem(key, JSON.stringify(value));`,
    example: `setLocal("user", { id: 1 })`,
  },
  {
    category: "📦 Storage",
    name: "getLocal",
    desc: "Read value from localStorage",
    code: `const getLocal = (key, fallback = null) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};`,
    example: `getLocal("user")`,
  },
  {
    category: "📦 Storage",
    name: "removeLocal",
    desc: "Remove key from localStorage",
    code: `const removeLocal = (key) =>
  localStorage.removeItem(key);`,
    example: `removeLocal("token")`,
  },
  {
    category: "📦 Storage",
    name: "clearLocal",
    desc: "Clear all localStorage",
    code: `const clearLocal = () =>
  localStorage.clear();`,
    example: `clearLocal()`,
  },
  {
    category: "📦 Storage",
    name: "setSession",
    desc: "Save value to sessionStorage",
    code: `const setSession = (key, value) =>
  sessionStorage.setItem(key, JSON.stringify(value));`,
    example: `setSession("step", 2)`,
  },
  {
    category: "📦 Storage",
    name: "getSession",
    desc: "Read value from sessionStorage",
    code: `const getSession = (key, fallback = null) => {
  try {
    return JSON.parse(sessionStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};`,
    example: `getSession("step")`,
  },
  {
    category: "📦 Storage",
    name: "removeSession",
    desc: "Remove key from sessionStorage",
    code: `const removeSession = (key) =>
  sessionStorage.removeItem(key);`,
    example: `removeSession("step")`,
  },
  {
    category: "📦 Storage",
    name: "isStorageAvailable",
    desc: "Checks if storage is available",
    code: `const isStorageAvailable = (type = "localStorage") => {
  try {
    const storage = window[type];
    const testKey = "__test__";
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};`,
    example: `isStorageAvailable() → true`,
  },
  {
    category: "📦 Storage",
    name: "setCookie",
    desc: "Set browser cookie",
    code: `const setCookie = (name, value, days = 7) => {
  const expires = new Date(
    Date.now() + days * 864e5
  ).toUTCString();
  document.cookie =
    name + "=" + value + "; expires=" + expires + "; path=/";
};`,
    example: `setCookie("theme", "dark")`,
  },
  {
    category: "📦 Storage",
    name: "getCookie",
    desc: "Get cookie by name",
    code: `const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="))
    ?.split("=")[1];`,
    example: `getCookie("theme")`,
  },
  {
    category: "📦 Storage",
    name: "deleteCookie",
    desc: "Delete cookie",
    code: `const deleteCookie = (name) =>
  setCookie(name, "", -1);`,
    example: `deleteCookie("theme")`,
  },
  {
    category: "📦 Storage",
    name: "rememberOnce",
    desc: "Run action only once per browser",
    code: `const rememberOnce = (key, fn) => {
  if (!localStorage.getItem(key)) {
    fn();
    localStorage.setItem(key, "true");
  }
};`,
    example: `rememberOnce("intro_shown", showIntro)`,
  },
  /* ================================
   📱 DEVICE / BROWSER HELPERS
================================ */

  {
    category: "📱 Device & Browser",
    name: "getDeviceType",
    desc: "Detects device type (mobile / tablet / desktop)",
    code: `const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad/.test(ua)) return "tablet";
  if (/mobile|android|iphone/.test(ua)) return "mobile";
  return "desktop";
};`,
    example: `getDeviceType() → "mobile"`,
  },
  {
    category: "📱 Device & Browser",
    name: "isMobile",
    desc: "Checks if device is mobile",
    code: `const isMobile = () =>
  /mobile|android|iphone/i.test(navigator.userAgent);`,
    example: `isMobile() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "isTablet",
    desc: "Checks if device is tablet",
    code: `const isTablet = () =>
  /tablet|ipad/i.test(navigator.userAgent);`,
    example: `isTablet() → false`,
  },
  {
    category: "📱 Device & Browser",
    name: "isDesktop",
    desc: "Checks if device is desktop",
    code: `const isDesktop = () =>
  !isMobile() && !isTablet();`,
    example: `isDesktop() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "getOS",
    desc: "Detects operating system",
    code: `const getOS = () => {
  const ua = navigator.userAgent;
  if (/windows/i.test(ua)) return "Windows";
  if (/mac/i.test(ua)) return "MacOS";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad/i.test(ua)) return "iOS";
  return "Unknown";
};`,
    example: `getOS() → "Android"`,
  },
  {
    category: "📱 Device & Browser",
    name: "getBrowser",
    desc: "Detects browser name",
    code: `const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edg")) return "Edge";
  return "Unknown";
};`,
    example: `getBrowser() → "Chrome"`,
  },
  {
    category: "📱 Device & Browser",
    name: "isOnline",
    desc: "Checks if user is online",
    code: `const isOnline = () =>
  navigator.onLine;`,
    example: `isOnline() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "onOnline",
    desc: "Runs callback when device goes online",
    code: `const onOnline = (cb) =>
  window.addEventListener("online", cb);`,
    example: `onOnline(() => console.log("Back online"))`,
  },
  {
    category: "📱 Device & Browser",
    name: "onOffline",
    desc: "Runs callback when device goes offline",
    code: `const onOffline = (cb) =>
  window.addEventListener("offline", cb);`,
    example: `onOffline(() => alert("No internet"))`,
  },
  {
    category: "📱 Device & Browser",
    name: "getViewport",
    desc: "Returns viewport width & height",
    code: `const getViewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});`,
    example: `getViewport() → { width: 390, height: 844 }`,
  },
  {
    category: "📱 Device & Browser",
    name: "isTouchDevice",
    desc: "Detects touch-enabled device",
    code: `const isTouchDevice = () =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;`,
    example: `isTouchDevice() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "prefersDarkMode",
    desc: "Checks if user prefers dark mode",
    code: `const prefersDarkMode = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;`,
    example: `prefersDarkMode() → true`,
  } /* ================================
   📱 DEVICE / BROWSER HELPERS
================================ */,

  {
    category: "📱 Device & Browser",
    name: "getDeviceType",
    desc: "Detects device type (mobile / tablet / desktop)",
    code: `const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad/.test(ua)) return "tablet";
  if (/mobile|android|iphone/.test(ua)) return "mobile";
  return "desktop";
};`,
    example: `getDeviceType() → "mobile"`,
  },
  {
    category: "📱 Device & Browser",
    name: "isMobile",
    desc: "Checks if device is mobile",
    code: `const isMobile = () =>
  /mobile|android|iphone/i.test(navigator.userAgent);`,
    example: `isMobile() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "isTablet",
    desc: "Checks if device is tablet",
    code: `const isTablet = () =>
  /tablet|ipad/i.test(navigator.userAgent);`,
    example: `isTablet() → false`,
  },
  {
    category: "📱 Device & Browser",
    name: "isDesktop",
    desc: "Checks if device is desktop",
    code: `const isDesktop = () =>
  !isMobile() && !isTablet();`,
    example: `isDesktop() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "getOS",
    desc: "Detects operating system",
    code: `const getOS = () => {
  const ua = navigator.userAgent;
  if (/windows/i.test(ua)) return "Windows";
  if (/mac/i.test(ua)) return "MacOS";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad/i.test(ua)) return "iOS";
  return "Unknown";
};`,
    example: `getOS() → "Android"`,
  },
  {
    category: "📱 Device & Browser",
    name: "getBrowser",
    desc: "Detects browser name",
    code: `const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edg")) return "Edge";
  return "Unknown";
};`,
    example: `getBrowser() → "Chrome"`,
  },
  {
    category: "📱 Device & Browser",
    name: "isOnline",
    desc: "Checks if user is online",
    code: `const isOnline = () =>
  navigator.onLine;`,
    example: `isOnline() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "onOnline",
    desc: "Runs callback when device goes online",
    code: `const onOnline = (cb) =>
  window.addEventListener("online", cb);`,
    example: `onOnline(() => console.log("Back online"))`,
  },
  {
    category: "📱 Device & Browser",
    name: "onOffline",
    desc: "Runs callback when device goes offline",
    code: `const onOffline = (cb) =>
  window.addEventListener("offline", cb);`,
    example: `onOffline(() => alert("No internet"))`,
  },
  {
    category: "📱 Device & Browser",
    name: "getViewport",
    desc: "Returns viewport width & height",
    code: `const getViewport = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});`,
    example: `getViewport() → { width: 390, height: 844 }`,
  },
  {
    category: "📱 Device & Browser",
    name: "isTouchDevice",
    desc: "Detects touch-enabled device",
    code: `const isTouchDevice = () =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;`,
    example: `isTouchDevice() → true`,
  },
  {
    category: "📱 Device & Browser",
    name: "prefersDarkMode",
    desc: "Checks if user prefers dark mode",
    code: `const prefersDarkMode = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;`,
    example: `prefersDarkMode() → true`,
  } /* ================================
   📊 ANALYTICS & EVENT HELPERS
================================ */,

  {
    category: "📊 Analytics & Tracking",
    name: "trackEvent",
    desc: "Tracks custom analytics event",
    code: `const trackEvent = (name, payload = {}) => {
  console.log("Event:", name, payload);
};`,
    example: `trackEvent("signup_completed")`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackPageView",
    desc: "Tracks page view",
    code: `const trackPageView = (path) => {
  console.log("Page view:", path);
};`,
    example: `trackPageView("/dashboard")`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackClick",
    desc: "Tracks button or link click",
    code: `const trackClick = (label) => {
  console.log("Clicked:", label);
};`,
    example: `trackClick("Buy Now Button")`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackFormSubmit",
    desc: "Tracks form submissions",
    code: `const trackFormSubmit = (formName) => {
  console.log("Form submitted:", formName);
};`,
    example: `trackFormSubmit("Login Form")`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackError",
    desc: "Tracks application errors",
    code: `const trackError = (error, context = {}) => {
  console.error("Tracked error:", error, context);
};`,
    example: `trackError(err, { page: "Checkout" })`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackTiming",
    desc: "Tracks performance timing",
    code: `const trackTiming = (label, ms) => {
  console.log("Timing:", label, ms + "ms");
};`,
    example: `trackTiming("API_CALL", 320)`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackConversion",
    desc: "Tracks conversion events",
    code: `const trackConversion = (name, value = 0) => {
  console.log("Conversion:", name, value);
};`,
    example: `trackConversion("Paid Plan", 499)`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackScrollDepth",
    desc: "Tracks scroll depth percentage",
    code: `const trackScrollDepth = (percent) => {
  console.log("Scrolled:", percent + "%");
};`,
    example: `trackScrollDepth(75)`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackSessionStart",
    desc: "Tracks session start",
    code: `const trackSessionStart = () => {
  console.log("Session started");
};`,
    example: `trackSessionStart()`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackSessionEnd",
    desc: "Tracks session end",
    code: `const trackSessionEnd = () => {
  console.log("Session ended");
};`,
    example: `trackSessionEnd()`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "trackUserProperty",
    desc: "Tracks user attributes",
    code: `const trackUserProperty = (key, value) => {
  console.log("User property:", key, value);
};`,
    example: `trackUserProperty("plan", "pro")`,
  },
  {
    category: "📊 Analytics & Tracking",
    name: "withTracking",
    desc: "Wraps function with analytics",
    code: `const withTracking = (fn, event) => {
  return (...args) => {
    console.log("Tracked:", event);
    return fn(...args);
  };
};`,
    example: `const trackedSave = withTracking(save, "save_clicked")`,
  } /* ================================
   🧩 FORM & UX HELPERS
================================ */,

  {
    category: "🧩 Form & UX",
    name: "preventSubmit",
    desc: "Prevents default form submission",
    code: `const preventSubmit = (e) => {
  e.preventDefault();
};`,
    example: `<form onSubmit={preventSubmit}>`,
  },
  {
    category: "🧩 Form & UX",
    name: "getInputValue",
    desc: "Safely gets input value",
    code: `const getInputValue = (e) => e?.target?.value ?? "";`,
    example: `onChange={(e) => setName(getInputValue(e))}`,
  },
  {
    category: "🧩 Form & UX",
    name: "autoFocusRef",
    desc: "Auto-focus input on mount",
    code: `const autoFocusRef = (ref) => {
  ref?.current?.focus();
};`,
    example: `useEffect(() => autoFocusRef(inputRef), [])`,
  },
  {
    category: "🧩 Form & UX",
    name: "disableWhileLoading",
    desc: "Disables button during loading",
    code: `const disableWhileLoading = (loading) => ({
  disabled: loading,
  style: { opacity: loading ? 0.6 : 1 }
});`,
    example: `<button {...disableWhileLoading(loading)} />`,
  },
  {
    category: "🧩 Form & UX",
    name: "scrollToTop",
    desc: "Smooth scroll to top",
    code: `const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};`,
    example: `scrollToTop()`,
  },
  {
    category: "🧩 Form & UX",
    name: "scrollToElement",
    desc: "Scrolls to specific element",
    code: `const scrollToElement = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};`,
    example: `scrollToElement("pricing")`,
  },
  {
    category: "🧩 Form & UX",
    name: "copyToClipboard",
    desc: "Copies text to clipboard",
    code: `const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};`,
    example: `copyToClipboard("Hello World")`,
  },
  {
    category: "🧩 Form & UX",
    name: "toggleBoolean",
    desc: "Toggles boolean state",
    code: `const toggleBoolean = (value) => !value;`,
    example: `setOpen(toggleBoolean(open))`,
  },
  {
    category: "🧩 Form & UX",
    name: "isSubmitDisabled",
    desc: "Checks if submit should be disabled",
    code: `const isSubmitDisabled = (values) =>
  Object.values(values).some(v => !v);`,
    example: `disabled={isSubmitDisabled(form)}`,
  },
  {
    category: "🧩 Form & UX",
    name: "limitCharacters",
    desc: "Limits text input length",
    code: `const limitCharacters = (text, limit) =>
  text.slice(0, limit);`,
    example: `setBio(limitCharacters(text, 160))`,
  },
  {
    category: "🧩 Form & UX",
    name: "showToast",
    desc: "Simple toast message",
    code: `const showToast = (msg) => {
  alert(msg);
};`,
    example: `showToast("Saved successfully")`,
  },
  {
    category: "🧩 Form & UX",
    name: "confirmAction",
    desc: "Confirms dangerous action",
    code: `const confirmAction = (msg = "Are you sure?") =>
  window.confirm(msg);`,
    example: `confirmAction() && deleteItem()`,
  } /* ================================
   📁 FILE / UPLOAD HELPERS
================================ */,

  {
    category: "📁 File & Upload",
    name: "getFileExtension",
    desc: "Returns file extension",
    code: `const getFileExtension = (fileName) =>
  fileName.split(".").pop();`,
    example: `getFileExtension("resume.pdf") → "pdf"`,
  },
  {
    category: "📁 File & Upload",
    name: "getFileSizeMB",
    desc: "Converts bytes to MB",
    code: `const getFileSizeMB = (bytes) =>
  (bytes / (1024 * 1024)).toFixed(2);`,
    example: `getFileSizeMB(1048576) → "1.00"`,
  },
  {
    category: "📁 File & Upload",
    name: "isValidFileType",
    desc: "Checks allowed file types",
    code: `const isValidFileType = (file, allowed) =>
  allowed.includes(file.type);`,
    example: `isValidFileType(file, ["image/png"])`,
  },
  {
    category: "📁 File & Upload",
    name: "isValidFileSize",
    desc: "Checks max file size (MB)",
    code: `const isValidFileSize = (file, maxMB) =>
  file.size <= maxMB * 1024 * 1024;`,
    example: `isValidFileSize(file, 5)`,
  },
  {
    category: "📁 File & Upload",
    name: "readFileAsText",
    desc: "Reads file as text",
    code: `const readFileAsText = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsText(file);
  });`,
    example: `await readFileAsText(file)`,
  },
  {
    category: "📁 File & Upload",
    name: "readFileAsBase64",
    desc: "Reads file as base64",
    code: `const readFileAsBase64 = (file) =>
  new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });`,
    example: `await readFileAsBase64(image)`,
  },
  {
    category: "📁 File & Upload",
    name: "downloadFile",
    desc: "Downloads file from URL",
    code: `const downloadFile = (url, name) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
};`,
    example: `downloadFile(url, "report.pdf")`,
  },
  {
    category: "📁 File & Upload",
    name: "downloadJSON",
    desc: "Downloads JSON as file",
    code: `const downloadJSON = (data, name="data.json") => {
  const blob = new Blob(
    [JSON.stringify(data, null, 2)],
    { type: "application/json" }
  );
  const url = URL.createObjectURL(blob);
  downloadFile(url, name);
};`,
    example: `downloadJSON(userData)`,
  },
  {
    category: "📁 File & Upload",
    name: "imagePreviewURL",
    desc: "Creates preview URL for image",
    code: `const imagePreviewURL = (file) =>
  URL.createObjectURL(file);`,
    example: `<img src={imagePreviewURL(file)} />`,
  },
  {
    category: "📁 File & Upload",
    name: "revokePreviewURL",
    desc: "Releases image preview memory",
    code: `const revokePreviewURL = (url) =>
  URL.revokeObjectURL(url);`,
    example: `revokePreviewURL(previewUrl)`,
  },
  {
    category: "📁 File & Upload",
    name: "isDragFile",
    desc: "Checks drag-drop file event",
    code: `const isDragFile = (e) =>
  e.dataTransfer?.files?.length > 0;`,
    example: `if (isDragFile(e)) handleDrop(e)`,
  },
  {
    category: "📁 File & Upload",
    name: "clearFileInput",
    desc: "Resets file input",
    code: `const clearFileInput = (ref) => {
  if (ref.current) ref.current.value = "";
};`,
    example: `clearFileInput(inputRef)`,
  } /* ================================
   🔐 AUTH / ROLE / PERMISSION
================================ */,

  {
    category: "🔐 Auth & Permissions",
    name: "isLoggedIn",
    desc: "Checks if user is logged in",
    code: `const isLoggedIn = (user) =>
  Boolean(user && user.id);`,
    example: `isLoggedIn(currentUser) → true`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "hasRole",
    desc: "Checks if user has specific role",
    code: `const hasRole = (user, role) =>
  user?.roles?.includes(role);`,
    example: `hasRole(user, "admin")`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "hasAnyRole",
    desc: "Checks if user has any role from list",
    code: `const hasAnyRole = (user, roles) =>
  roles.some(r => user?.roles?.includes(r));`,
    example: `hasAnyRole(user, ["admin","editor"])`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "hasAllRoles",
    desc: "Checks if user has all roles",
    code: `const hasAllRoles = (user, roles) =>
  roles.every(r => user?.roles?.includes(r));`,
    example: `hasAllRoles(user, ["admin","owner"])`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "canAccess",
    desc: "Checks permission-based access",
    code: `const canAccess = (user, permission) =>
  user?.permissions?.includes(permission);`,
    example: `canAccess(user, "EDIT_POST")`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "isAdmin",
    desc: "Shortcut to check admin role",
    code: `const isAdmin = (user) =>
  user?.roles?.includes("admin");`,
    example: `isAdmin(user)`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "isOwner",
    desc: "Checks resource ownership",
    code: `const isOwner = (user, resource) =>
  user?.id === resource?.ownerId;`,
    example: `isOwner(user, post)`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "canEditResource",
    desc: "Checks edit permission or ownership",
    code: `const canEditResource = (user, resource) =>
  isAdmin(user) || isOwner(user, resource);`,
    example: `canEditResource(user, post)`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "canViewRoute",
    desc: "Checks route access based on roles",
    code: `const canViewRoute = (user, allowedRoles) =>
  allowedRoles.some(r => user?.roles?.includes(r));`,
    example: `canViewRoute(user, ["admin","manager"])`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "requireAuth",
    desc: "Protects routes from unauth users",
    code: `const requireAuth = (user, cb) => {
  if (!user) throw new Error("Unauthorized");
  return cb();
};`,
    example: `requireAuth(user, loadDashboard)`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "logoutUser",
    desc: "Clears auth state",
    code: `const logoutUser = (setUser) => {
  setUser(null);
  localStorage.clear();
};`,
    example: `logoutUser(setUser)`,
  },
  {
    category: "🔐 Auth & Permissions",
    name: "getUserInitials",
    desc: "Returns initials from name",
    code: `const getUserInitials = (name) =>
  name?.split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase();`,
    example: `getUserInitials("John Doe") → "JD"`,
  } /* ================================
   🧠 STATE / STORAGE / CACHE
================================ */,

  {
    category: "🧠 State & Storage",
    name: "setLocal",
    desc: "Save value to localStorage",
    code: `const setLocal = (key, value) =>
  localStorage.setItem(key, JSON.stringify(value));`,
    example: `setLocal("token", "abc123")`,
  },
  {
    category: "🧠 State & Storage",
    name: "getLocal",
    desc: "Read value from localStorage",
    code: `const getLocal = (key, fallback = null) => {
  const val = localStorage.getItem(key);
  return val ? JSON.parse(val) : fallback;
};`,
    example: `getLocal("token")`,
  },
  {
    category: "🧠 State & Storage",
    name: "removeLocal",
    desc: "Remove key from localStorage",
    code: `const removeLocal = (key) =>
  localStorage.removeItem(key);`,
    example: `removeLocal("token")`,
  },
  {
    category: "🧠 State & Storage",
    name: "clearLocal",
    desc: "Clear entire localStorage",
    code: `const clearLocal = () =>
  localStorage.clear();`,
    example: `clearLocal()`,
  },
  {
    category: "🧠 State & Storage",
    name: "setSession",
    desc: "Save value to sessionStorage",
    code: `const setSession = (key, value) =>
  sessionStorage.setItem(key, JSON.stringify(value));`,
    example: `setSession("step", 2)`,
  },
  {
    category: "🧠 State & Storage",
    name: "getSession",
    desc: "Read value from sessionStorage",
    code: `const getSession = (key, fallback = null) => {
  const val = sessionStorage.getItem(key);
  return val ? JSON.parse(val) : fallback;
};`,
    example: `getSession("step")`,
  },
  {
    category: "🧠 State & Storage",
    name: "setWithExpiry",
    desc: "Store data with expiry time (TTL)",
    code: `const setWithExpiry = (key, value, ttl) => {
  const data = {
    value,
    expiry: Date.now() + ttl
  };
  localStorage.setItem(key, JSON.stringify(data));
};`,
    example: `setWithExpiry("cache", data, 60000)`,
  },
  {
    category: "🧠 State & Storage",
    name: "getWithExpiry",
    desc: "Read data that expires automatically",
    code: `const getWithExpiry = (key) => {
  const data = JSON.parse(localStorage.getItem(key));
  if (!data) return null;
  if (Date.now() > data.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return data.value;
};`,
    example: `getWithExpiry("cache")`,
  },
  {
    category: "🧠 State & Storage",
    name: "rememberOnce",
    desc: "Runs function only once",
    code: `const rememberOnce = (key, fn) => {
  if (localStorage.getItem(key)) return;
  fn();
  localStorage.setItem(key, "done");
};`,
    example: `rememberOnce("introShown", showIntro)`,
  },
  {
    category: "🧠 State & Storage",
    name: "toggleBoolean",
    desc: "Toggle boolean state value",
    code: `const toggleBoolean = (value) =>
  !value;`,
    example: `toggleBoolean(isOpen)`,
  },
  {
    category: "🧠 State & Storage",
    name: "safeJSONParse",
    desc: "Safely parse JSON",
    code: `const safeJSONParse = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};`,
    example: `safeJSONParse(badJson, {})`,
  },
  {
    category: "🧠 State & Storage",
    name: "memoize",
    desc: "Caches function results",
    code: `const memoize = (fn) => {
  const cache = {};
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache[key]) return cache[key];
    return (cache[key] = fn(...args));
  };
};`,
    example: `const fastFn = memoize(expensiveFn)`,
  } /* ==================================
   📱 MOBILE DEVELOPMENT HELPERS
================================== */,

  {
    category: "📱 Mobile Development",
    name: "isIOS",
    desc: "Check if device is iOS",
    code: `import { Platform } from "react-native";
const isIOS = Platform.OS === "ios";`,
    example: `isIOS → true`,
  },
  {
    category: "📱 Mobile Development",
    name: "isAndroid",
    desc: "Check if device is Android",
    code: `import { Platform } from "react-native";
const isAndroid = Platform.OS === "android";`,
    example: `isAndroid → true`,
  },
  {
    category: "📱 Mobile Development",
    name: "getPlatform",
    desc: "Returns current platform",
    code: `import { Platform } from "react-native";
const getPlatform = () => Platform.OS;`,
    example: `getPlatform() → "ios"`,
  },
  {
    category: "📱 Mobile Development",
    name: "responsiveSize",
    desc: "Scale UI based on screen width",
    code: `import { Dimensions } from "react-native";
const { width } = Dimensions.get("window");
const responsiveSize = (size) => (width / 375) * size;`,
    example: `fontSize: responsiveSize(16)`,
  },
  {
    category: "📱 Mobile Development",
    name: "vh",
    desc: "Height percentage helper",
    code: `import { Dimensions } from "react-native";
const vh = (percent) =>
  (Dimensions.get("window").height * percent) / 100;`,
    example: `height: vh(30)`,
  },
  {
    category: "📱 Mobile Development",
    name: "vw",
    desc: "Width percentage helper",
    code: `import { Dimensions } from "react-native";
const vw = (percent) =>
  (Dimensions.get("window").width * percent) / 100;`,
    example: `width: vw(50)`,
  },
  {
    category: "📱 Mobile Development",
    name: "isSmallDevice",
    desc: "Detect small screens",
    code: `import { Dimensions } from "react-native";
const isSmallDevice =
  Dimensions.get("window").width < 360;`,
    example: `isSmallDevice → true`,
  },
  {
    category: "📱 Mobile Development",
    name: "safeHitSlop",
    desc: "Increase touchable area safely",
    code: `const safeHitSlop = {
  top: 10,
  bottom: 10,
  left: 10,
  right: 10,
};`,
    example: `<TouchableOpacity hitSlop={safeHitSlop} />`,
  },
  {
    category: "📱 Mobile Development",
    name: "keyboardAvoid",
    desc: "Keyboard vertical offset helper",
    code: `import { Platform } from "react-native";
const keyboardOffset = Platform.OS === "ios" ? 80 : 0;`,
    example: `<KeyboardAvoidingView keyboardVerticalOffset={keyboardOffset} />`,
  },
  {
    category: "📱 Mobile Development",
    name: "openExternalLink",
    desc: "Open URL in browser",
    code: `import { Linking } from "react-native";
const openExternalLink = (url) => Linking.openURL(url);`,
    example: `openExternalLink("https://google.com")`,
  },
  {
    category: "📱 Mobile Development",
    name: "callPhone",
    desc: "Trigger phone call",
    code: `import { Linking } from "react-native";
const callPhone = (number) =>
  Linking.openURL(\`tel:\${number}\`);`,
    example: `callPhone("9876543210")`,
  },
  {
    category: "📱 Mobile Development",
    name: "sendSMS",
    desc: "Send SMS from app",
    code: `import { Linking } from "react-native";
const sendSMS = (number) =>
  Linking.openURL(\`sms:\${number}\`);`,
    example: `sendSMS("9876543210")`,
  },
  {
    category: "📱 Mobile Development",
    name: "preventDoubleTap",
    desc: "Prevent multiple button taps",
    code: `let lastTap = 0;
const preventDoubleTap = (fn, delay = 500) => {
  const now = Date.now();
  if (now - lastTap < delay) return;
  lastTap = now;
  fn();
};`,
    example: `preventDoubleTap(onPress)`,
  },
  {
    category: "📱 Mobile Development",
    name: "normalizeFont",
    desc: "Normalize font size across devices",
    code: `import { Dimensions, PixelRatio } from "react-native";
const normalizeFont = (size) => {
  const scale = Dimensions.get("window").width / 375;
  return Math.round(PixelRatio.roundToNearestPixel(size * scale));
};`,
    example: `fontSize: normalizeFont(14)`,
  } /* ==================================
   📡 NETWORK & OFFLINE MODE HELPERS
================================== */,

  {
    category: "📡 Network & Offline",
    name: "isOnline",
    desc: "Check if device is online (web)",
    code: `const isOnline = () => navigator.onLine;`,
    example: `isOnline() → true`,
  },
  {
    category: "📡 Network & Offline",
    name: "onNetworkChange",
    desc: "Listen to network status changes",
    code: `const onNetworkChange = (cb) => {
  window.addEventListener("online", () => cb(true));
  window.addEventListener("offline", () => cb(false));
};`,
    example: `onNetworkChange(status => console.log(status))`,
  },
  {
    category: "📡 Network & Offline",
    name: "withTimeout",
    desc: "Reject promise if timeout exceeds",
    code: `const withTimeout = (promise, ms = 5000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject("Timeout"), ms)
    ),
  ]);`,
    example: `withTimeout(fetch(url), 3000)`,
  },
  {
    category: "📡 Network & Offline",
    name: "retryRequest",
    desc: "Retry API request on failure",
    code: `const retryRequest = async (fn, retries = 3) => {
  try {
    return await fn();
  } catch (e) {
    if (retries <= 1) throw e;
    return retryRequest(fn, retries - 1);
  }
};`,
    example: `retryRequest(() => fetchData())`,
  },
  {
    category: "📡 Network & Offline",
    name: "safeFetch",
    desc: "Fetch with error handling",
    code: `const safeFetch = async (url, options) => {
  try {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error("Request failed");
    return await res.json();
  } catch (e) {
    return { error: true, message: e.message };
  }
};`,
    example: `safeFetch("/api/users")`,
  },
  {
    category: "📡 Network & Offline",
    name: "buildHeaders",
    desc: "Build API headers dynamically",
    code: `const buildHeaders = (token) => ({
  "Content-Type": "application/json",
  ...(token && { Authorization: \`Bearer \${token}\` }),
});`,
    example: `buildHeaders(authToken)`,
  },
  {
    category: "📡 Network & Offline",
    name: "serializeParams",
    desc: "Convert object to query string",
    code: `const serializeParams = (params) =>
  new URLSearchParams(params).toString();`,
    example: `serializeParams({ page:1, limit:10 })`,
  },
  {
    category: "📡 Network & Offline",
    name: "cacheResponse",
    desc: "Cache API response in localStorage",
    code: `const cacheResponse = (key, data) =>
  localStorage.setItem(key, JSON.stringify(data));`,
    example: `cacheResponse("users", users)`,
  },
  {
    category: "📡 Network & Offline",
    name: "getCachedResponse",
    desc: "Retrieve cached API response",
    code: `const getCachedResponse = (key) =>
  JSON.parse(localStorage.getItem(key));`,
    example: `getCachedResponse("users")`,
  },
  {
    category: "📡 Network & Offline",
    name: "clearCache",
    desc: "Clear cached API data",
    code: `const clearCache = (key) =>
  localStorage.removeItem(key);`,
    example: `clearCache("users")`,
  },
  {
    category: "📡 Network & Offline",
    name: "offlineFallback",
    desc: "Return cached data if offline",
    code: `const offlineFallback = async (key, fetchFn) => {
  if (!navigator.onLine) return getCachedResponse(key);
  const data = await fetchFn();
  cacheResponse(key, data);
  return data;
};`,
    example: `offlineFallback("posts", fetchPosts)`,
  },
  {
    category: "📡 Network & Offline",
    name: "delayRequest",
    desc: "Artificial API delay (mock)",
    code: `const delayRequest = (ms = 500) =>
  new Promise(res => setTimeout(res, ms));`,
    example: `await delayRequest(800)`,
  },
  {
    category: "📡 Network & Offline",
    name: "isSlowNetwork",
    desc: "Detect slow connection (browser)",
    code: `const isSlowNetwork = () =>
  navigator.connection?.effectiveType === "2g";`,
    example: `isSlowNetwork() → false`,
  },
  {
    category: "📡 Network & Offline",
    name: "abortableFetch",
    desc: "Cancel fetch request",
    code: `const abortableFetch = (url) => {
  const controller = new AbortController();
  fetch(url, { signal: controller.signal });
  return controller;
};`,
    example: `const ctrl = abortableFetch(url); ctrl.abort();`,
  } /* ==================================
   📱 MOBILE DEVELOPMENT HELPERS
   (React Native / Expo friendly)
================================== */,

  {
    category: "📱 Mobile Development",
    name: "isAndroid",
    desc: "Check if device is Android",
    code: `import { Platform } from "react-native";
const isAndroid = () => Platform.OS === "android";`,
    example: `isAndroid() → true`,
  },
  {
    category: "📱 Mobile Development",
    name: "isIOS",
    desc: "Check if device is iOS",
    code: `import { Platform } from "react-native";
const isIOS = () => Platform.OS === "ios";`,
    example: `isIOS() → false`,
  },
  {
    category: "📱 Mobile Development",
    name: "getDeviceOS",
    desc: "Get current mobile OS",
    code: `import { Platform } from "react-native";
const getDeviceOS = () => Platform.OS;`,
    example: `getDeviceOS() → "android"`,
  },
  {
    category: "📱 Mobile Development",
    name: "hp",
    desc: "Responsive height percentage",
    code: `import { Dimensions } from "react-native";
const hp = (percent) =>
  Dimensions.get("window").height * (percent / 100);`,
    example: `height: hp(30)`,
  },
  {
    category: "📱 Mobile Development",
    name: "wp",
    desc: "Responsive width percentage",
    code: `import { Dimensions } from "react-native";
const wp = (percent) =>
  Dimensions.get("window").width * (percent / 100);`,
    example: `width: wp(80)`,
  },
  {
    category: "📱 Mobile Development",
    name: "dismissKeyboard",
    desc: "Dismiss mobile keyboard",
    code: `import { Keyboard } from "react-native";
const dismissKeyboard = () => Keyboard.dismiss();`,
    example: `dismissKeyboard()`,
  },
  {
    category: "📱 Mobile Development",
    name: "keyboardAwarePadding",
    desc: "Adjust padding when keyboard opens",
    code: `import { Keyboard } from "react-native";
const keyboardAwarePadding = (setPadding) => {
  Keyboard.addListener("keyboardDidShow", () => setPadding(300));
  Keyboard.addListener("keyboardDidHide", () => setPadding(0));
};`,
    example: `keyboardAwarePadding(setBottom)`,
  },
  {
    category: "📱 Mobile Development",
    name: "openDialer",
    desc: "Open phone dialer",
    code: `import { Linking } from "react-native";
const openDialer = (phone) =>
  Linking.openURL(\`tel:\${phone}\`);`,
    example: `openDialer("9876543210")`,
  },
  {
    category: "📱 Mobile Development",
    name: "openEmail",
    desc: "Open email app",
    code: `import { Linking } from "react-native";
const openEmail = (email) =>
  Linking.openURL(\`mailto:\${email}\`);`,
    example: `openEmail("test@gmail.com")`,
  },
  {
    category: "📱 Mobile Development",
    name: "openMap",
    desc: "Open maps with coordinates",
    code: `import { Linking } from "react-native";
const openMap = (lat, lng) =>
  Linking.openURL(
    \`https://www.google.com/maps?q=\${lat},\${lng}\`
  );`,
    example: `openMap(28.61, 77.23)`,
  },
  {
    category: "📱 Mobile Development",
    name: "preventDoubleTap",
    desc: "Prevent double button press",
    code: `let lastTap = 0;
const preventDoubleTap = (fn, delay = 500) => {
  const now = Date.now();
  if (now - lastTap < delay) return;
  lastTap = now;
  fn();
};`,
    example: `preventDoubleTap(onSubmit)`,
  },
  {
    category: "📱 Mobile Development",
    name: "vibrate",
    desc: "Trigger device vibration",
    code: `import { Vibration } from "react-native";
const vibrate = (ms = 100) => Vibration.vibrate(ms);`,
    example: `vibrate(200)`,
  },
  {
    category: "📱 Mobile Development",
    name: "copyToClipboard",
    desc: "Copy text to clipboard",
    code: `import * as Clipboard from "expo-clipboard";
const copyToClipboard = async (text) =>
  await Clipboard.setStringAsync(text);`,
    example: `copyToClipboard("Hello")`,
  },
  {
    category: "📱 Mobile Development",
    name: "getBatteryLevel",
    desc: "Get device battery level (Expo)",
    code: `import * as Battery from "expo-battery";
const getBatteryLevel = async () =>
  await Battery.getBatteryLevelAsync();`,
    example: `getBatteryLevel() → 0.87`,
  },
  {
    category: "📱 Mobile Development",
    name: "lockOrientation",
    desc: "Lock screen orientation",
    code: `import * as ScreenOrientation from "expo-screen-orientation";
const lockOrientation = async () =>
  ScreenOrientation.lockAsync(
    ScreenOrientation.OrientationLock.PORTRAIT
  );`,
    example: `lockOrientation()`,
  },
  {
    category: "📱 Mobile Development",
    name: "unlockOrientation",
    desc: "Unlock screen orientation",
    code: `import * as ScreenOrientation from "expo-screen-orientation";
const unlockOrientation = async () =>
  ScreenOrientation.unlockAsync();`,
    example: `unlockOrientation()`,
  },
  {
    category: "📱 Mobile Development",
    name: "isTablet",
    desc: "Detect tablet device",
    code: `import * as Device from "expo-device";
const isTablet = () => Device.deviceType === 2;`,
    example: `isTablet() → false`,
  },
  {
    category: "📱 Mobile Development",
    name: "safeAreaPadding",
    desc: "Safe padding for notch devices",
    code: `import { useSafeAreaInsets } from "react-native-safe-area-context";
const safeAreaPadding = () => {
  const insets = useSafeAreaInsets();
  return { paddingTop: insets.top };
};`,
    example: `style={safeAreaPadding()}`,
  } /* ==================================
   🔐 AUTH & SECURITY HELPERS
================================== */,

  {
    category: "🔐 Auth & Security",
    name: "getAuthToken",
    desc: "Get auth token from localStorage",
    code: `const getAuthToken = () => localStorage.getItem("token");`,
    example: `getAuthToken() → "eyJhbGciOiJIUzI1Ni..."`,
  },
  {
    category: "🔐 Auth & Security",
    name: "setAuthToken",
    desc: "Save auth token securely",
    code: `const setAuthToken = (token) =>
  localStorage.setItem("token", token);`,
    example: `setAuthToken(token)`,
  },
  {
    category: "🔐 Auth & Security",
    name: "removeAuthToken",
    desc: "Clear auth token on logout",
    code: `const removeAuthToken = () =>
  localStorage.removeItem("token");`,
    example: `removeAuthToken()`,
  },
  {
    category: "🔐 Auth & Security",
    name: "isLoggedIn",
    desc: "Check if user is logged in",
    code: `const isLoggedIn = () => !!localStorage.getItem("token");`,
    example: `isLoggedIn() → true`,
  },
  {
    category: "🔐 Auth & Security",
    name: "parseJWT",
    desc: "Decode JWT payload safely",
    code: `const parseJWT = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};`,
    example: `parseJWT(token).role → "admin"`,
  },
  {
    category: "🔐 Auth & Security",
    name: "isTokenExpired",
    desc: "Check if JWT token is expired",
    code: `const isTokenExpired = (token) => {
  const data = parseJWT(token);
  if (!data?.exp) return true;
  return Date.now() >= data.exp * 1000;
};`,
    example: `isTokenExpired(token) → false`,
  },
  {
    category: "🔐 Auth & Security",
    name: "getUserRole",
    desc: "Extract user role from JWT",
    code: `const getUserRole = (token) => {
  const data = parseJWT(token);
  return data?.role || null;
};`,
    example: `getUserRole(token) → "admin"`,
  },
  {
    category: "🔐 Auth & Security",
    name: "hasRole",
    desc: "Check if user has required role",
    code: `const hasRole = (token, role) =>
  getUserRole(token) === role;`,
    example: `hasRole(token, "admin") → true`,
  },
  {
    category: "🔐 Auth & Security",
    name: "hasAnyRole",
    desc: "Check if user has one of allowed roles",
    code: `const hasAnyRole = (token, roles = []) =>
  roles.includes(getUserRole(token));`,
    example: `hasAnyRole(token, ["admin","manager"])`,
  },
  {
    category: "🔐 Auth & Security",
    name: "hasPermission",
    desc: "Permission-based access check",
    code: `const hasPermission = (user, permission) =>
  user?.permissions?.includes(permission);`,
    example: `hasPermission(user,"CAN_EDIT")`,
  },
  {
    category: "🔐 Auth & Security",
    name: "authGuard",
    desc: "Protect route access",
    code: `const authGuard = (navigate) => {
  if (!isLoggedIn()) navigate("/login");
};`,
    example: `authGuard(navigate)`,
  },
  {
    category: "🔐 Auth & Security",
    name: "sanitizeInput",
    desc: "Prevent XSS by sanitizing input",
    code: `const sanitizeInput = (str) =>
  str.replace(/[<>]/g, "");`,
    example: `sanitizeInput("<script>") → "script"`,
  },
  {
    category: "🔐 Auth & Security",
    name: "hashPasswordClient",
    desc: "Basic client-side hashing (not replacement for backend)",
    code: `const hashPasswordClient = async (pwd) => {
  const msgUint8 = new TextEncoder().encode(pwd);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
};`,
    example: `hashPasswordClient("123456")`,
  },
  {
    category: "🔐 Auth & Security",
    name: "generateCSRFToken",
    desc: "Generate random CSRF token",
    code: `const generateCSRFToken = () =>
  Math.random().toString(36).substring(2);`,
    example: `generateCSRFToken()`,
  },
  {
    category: "🔐 Auth & Security",
    name: "secureCompare",
    desc: "Constant-time string comparison",
    code: `const secureCompare = (a, b) => {
  if (a.length !== b.length) return false;
  let res = 0;
  for (let i = 0; i < a.length; i++) {
    res |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return res === 0;
};`,
    example: `secureCompare("abc","abc") → true`,
  },
  {
    category: "🔐 Auth & Security",
    name: "rateLimitClient",
    desc: "Client-side rate limit helper",
    code: `let lastAction = 0;
const rateLimitClient = (delay = 1000) => {
  const now = Date.now();
  if (now - lastAction < delay) return false;
  lastAction = now;
  return true;
};`,
    example: `rateLimitClient(2000) → true`,
  },
];

/* ================== STYLES ================== */

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background: #020617;
  color: #e5e7eb;
`;
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 24px 0 40px;
`;

const FilterChip = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid #1e293b;

  background: ${({ active }) =>
    active ? "linear-gradient(135deg,#3b82f6,#2563eb)" : "#020617"};
  color: ${({ active }) => (active ? "white" : "#94a3b8")};

  &:hover {
    border-color: #3b82f6;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 1000;
`;

const Subtitle = styled.p`
  opacity: 0.75;
  margin-bottom: 40px;
`;

const SearchBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  background: #020617;
  border: 1px solid #1e293b;
  margin-bottom: 40px;
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: white;
  outline: none;
`;

const Category = styled.h2`
  margin: 50px 0 20px;
  color: #60a5fa;
`;

const Card = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.2);
`;

const HelperTitle = styled.h3`
  font-size: 18px;
  font-weight: 900;
`;

const HelperDesc = styled.p`
  opacity: 0.85;
  margin: 6px 0 12px;
`;

const Code = styled.pre`
  background: #020617;
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 16px;
  overflow-x: auto;
`;

const Demo = styled.div`
  margin-top: 12px;
  font-size: 14px;
  span {
    color: #22c55e;
    font-weight: 700;
  }
`;
const CodeWrap = styled.div`
  position: relative;
  margin-top: 14px;
`;

const CodeBlock = styled.pre`
  padding: 18px 16px;
  border-radius: 14px;
  background: #020617;
  border: 1px solid #1e293b;
  font-size: 14px;
  line-height: 1.6;
  overflow-x: auto;
  color: #e5e7eb;
  font-family: "Fira Code", monospace;
`;

const CopyBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(59, 130, 246, 0.15);
  border: none;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
  color: #60a5fa;
  font-size: 12px;

  &:hover {
    background: rgba(59, 130, 246, 0.25);
  }
`;
const ExampleBox = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(34, 197, 94, 0.08);
  border-left: 4px solid #22c55e;
  font-size: 14px;
  line-height: 1.6;
`;

const ExampleLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 6px;
  color: #22c55e;
  text-transform: uppercase;
`;

const ExampleText = styled.div`
  color: #e5e7eb;

  span {
    color: #22c55e;
    font-weight: 800;
  }
`;

/* ================== PAGE ================== */

export default function DevHelpersPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("ALL");

  const filtered = useMemo(
    () =>
      HELPERS.filter(
        (h) =>
          h.name.toLowerCase().includes(search.toLowerCase()) ||
          h.category.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  const filteredHelpers = useMemo(() => {
    return HELPERS.filter((h) => {
      const matchSearch =
        h.name.toLowerCase().includes(search.toLowerCase()) ||
        h.desc.toLowerCase().includes(search.toLowerCase());

      const matchCategory =
        activeCategory === "ALL" || h.category === activeCategory;

      return matchSearch && matchCategory;
    });
  }, [search, activeCategory]);
  const groupedHelpers = useMemo(() => {
    return filteredHelpers.reduce((acc, item) => {
      acc[item.category] = acc[item.category] || [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [filteredHelpers]);

  const grouped = filtered.reduce((acc, h) => {
    acc[h.category] = acc[h.category] || [];
    acc[h.category].push(h);
    return acc;
  }, {});

  const categories = [
    "ALL",
    ...Array.from(new Set(HELPERS.map((h) => h.category))),
  ];
  const copy = (t) => navigator.clipboard.writeText(t);

  return (
    <Page>
      <Container>
        <Title>🧰 Developer Helper Functions</Title>
        <Subtitle>
          50+ copy-paste ready utilities with live demos — real world &
          interview ready.
        </Subtitle>

        <SearchBox>
          <Search size={18} />
          <Input
            placeholder="Search helper..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBox>
        <FilterBar>
          {categories.map((cat) => (
            <FilterChip
              key={cat}
              active={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </FilterChip>
          ))}
        </FilterBar>

        {Object.entries(groupedHelpers).map(([category, items]) => (
          <div key={category}>
            <Category>{category}</Category>

            {items.map((h, i) => (
              <Card key={i}>
                <HelperTitle>{h.name}</HelperTitle>
                <HelperDesc>{h.desc}</HelperDesc>

                <CodeWrap>
                  <CopyBtn onClick={() => copy(h.code)}>Copy</CopyBtn>
                  <CodeBlock>{h.code}</CodeBlock>
                </CodeWrap>

                <ExampleBox>
                  <ExampleLabel>Example</ExampleLabel>
                  <ExampleText>
                    <span>{h.example}</span>
                  </ExampleText>
                </ExampleBox>
              </Card>
            ))}
          </div>
        ))}
      </Container>
    </Page>
  );
}
